// Typed API client — all backend calls go through here
import type { DriverDef } from './fpaDrivers';
export type { DriverDef } from './fpaDrivers';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

// Auth identity is set by AuthProvider and read on every request. In dev it
// drives X-Dev-User-Id (consumed by the backend auth middleware). In any env,
// impersonationUserId drives X-Impersonate-User-Id so admins can act as
// another user; the backend only honours it for admin callers.
let currentUserId: string | null = null;
let impersonationUserId: string | null = null;
let currentGoogleToken: string | null = null;

export function setAuthIdentity(opts: { userId: string | null; impersonating: string | null; googleToken?: string | null }) {
  currentUserId = opts.userId;
  impersonationUserId = opts.impersonating;
  if (opts.googleToken !== undefined) currentGoogleToken = opts.googleToken;
}

// ─── Token-refresh hook ─────────────────────────────────────────────────────
// On 401 (typical cause: Google ID token past its 1-hour exp), we let the SSO
// provider do a silent One-Tap renewal and retry the request once with the
// fresh token. This avoids bouncing the user to the login screen mid-session.
// The refresher is registered by <GoogleSilentRefresh>; in dev mode (no SSO)
// it stays null and 401s propagate normally.
type TokenRefresher = () => Promise<string | null>;
let tokenRefresher: TokenRefresher | null = null;

export function setTokenRefresher(fn: TokenRefresher | null) {
  tokenRefresher = fn;
}

// ─── Auth-failure hook ───────────────────────────────────────────────────────
// Called when an SSO-mode request lands a final 401 that silent refresh
// couldn't recover from (FedCM disabled by the browser, third-party cookies
// blocked, user revoked the OAuth grant, server clock skew, …). The handler —
// registered by AuthProvider — clears the stored token and flips the auth
// state to `needsLogin`, which renders the LoginScreen instead of leaving
// the page stuck with a half-loaded shell and an opaque "HTTP 401" toast.
let onAuthFailure: (() => void) | null = null;
// Suppresses repeat fires when several concurrent requests all 401 at once.
// Cleared by setOnAuthFailure (so a fresh sign-in re-arms the trap).
let authFailureFired = false;

export function setOnAuthFailure(fn: (() => void) | null) {
  onAuthFailure = fn;
  authFailureFired = false;
}

interface RequestOptions extends RequestInit {
  /** Suppress the X-Impersonate-User-Id header on this call (used by /impersonate/stop). */
  skipImpersonation?: boolean;
  /** Internal — set when a request is the retry after a silent token refresh. */
  _retried?: boolean;
}

async function request<T>(path: string, options?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  };
  // SSO mode: send Google ID token as Bearer. Dev mode: send X-Dev-User-Id.
  if (currentGoogleToken) {
    headers['Authorization'] = `Bearer ${currentGoogleToken}`;
  } else if (currentUserId) {
    headers['X-Dev-User-Id'] = currentUserId;
  }
  if (impersonationUserId && !options?.skipImpersonation) {
    headers['X-Impersonate-User-Id'] = impersonationUserId;
  }
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
  });

  // Expired-token silent retry. We only ask the refresher for a new token if
  // (a) we were sending a Google token, (b) this isn't already a retry, and
  // (c) the SSO provider has registered itself. If refresh fails, the 401
  // propagates normally and the app falls through to the login screen.
  //
  // The refresher's onSuccess path already calls setAuthIdentity, which
  // updates `currentGoogleToken` before the promise resolves — so we don't
  // need to compare the returned token to the "old" value here. The
  // `_retried` guard prevents an infinite loop if the new token is still
  // being rejected by the backend. Any truthy token returned means the
  // refresh succeeded and we should retry the original request.
  if (
    res.status === 401 &&
    !options?._retried &&
    tokenRefresher &&
    currentGoogleToken
  ) {
    const newToken = await tokenRefresher();
    if (newToken) {
      return request<T>(path, { ...options, _retried: true });
    }
  }

  // Final-401 escape hatch. If we're in SSO mode and either (a) silent refresh
  // exhausted, or (b) it never got a chance (no refresher registered), the
  // session is effectively dead — every subsequent request will also 401. Tell
  // AuthProvider to drop the stored token so the LoginScreen takes over.
  // Skipped in dev mode (no Google token in flight) so a 401 from a misrouted
  // dev request doesn't kick a real user out for no reason.
  if (
    res.status === 401 &&
    currentGoogleToken &&
    onAuthFailure &&
    !authFailureFired
  ) {
    authFailureFired = true;
    onAuthFailure();
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** Download an auth-aware file. Used for CSV exports — we can't just use a
 *  plain <a href="/api/..."> because the auth headers (Bearer / X-Dev-User-Id /
 *  X-Impersonate-User-Id) need to travel with the request. Instead we fetch
 *  as a blob and synthesize a temporary download link. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (currentGoogleToken) headers['Authorization'] = `Bearer ${currentGoogleToken}`;
  else if (currentUserId) headers['X-Dev-User-Id'] = currentUserId;
  if (impersonationUserId) headers['X-Impersonate-User-Id'] = impersonationUserId;
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click flushes so iOS Safari has time to pick it up.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Types (mirrored from API — could be shared package later) ─────────────

export type Role = 'IC' | 'DM' | 'SA' | 'DIRECTOR' | 'EXEC' | 'OPS' | 'SUPPORT';
export type BonusRole = Extract<Role, 'IC' | 'DM' | 'SA'>;

// ─── FP&A access control ────────────────────────────────────────────────────
export type FpaRole = 'viewer' | 'analyst' | 'approver';
export type FpaScopeType = 'company' | 'division' | 'department';

export interface FpaScopeGrant {
  id: string;
  scope_type: FpaScopeType;
  scope_id: string | null;
}
/** Resolved data scope. v1: `company` true for any granted user. */
export interface FpaScope {
  company: boolean;
  divisions: string[];
  departments: string[];
}
export interface FpaMe {
  fpa_role: FpaRole | null;
  scope: FpaScope;
}
export interface FpaUserRow {
  jira_user_id: string;
  display_name: string;
  email: string;
  role: Role;
  is_admin: boolean;
  fpa_role: FpaRole | null;
  scopes: FpaScopeGrant[];
}
export interface FpaAuditEntry {
  id: string;
  actor_id: string;
  actor_name: string | null;
  real_actor_id: string | null;
  real_actor_name: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before_json: unknown;
  after_json: unknown;
  scope_type: string | null;
  scope_id: string | null;
  at: string;
}

// ─── FP&A — QBO actuals (Phase 2) ───────────────────────────────────────────
export interface FpaActualsCell {
  period_month: string;
  line_code: string;
  department_id: string | null;
  amount: number;
}
export interface FpaActualsReport {
  from: string;
  to: string;
  cells: FpaActualsCell[];
  unmapped: {
    accounts: Array<{ qbo_account_id: string; name: string | null; classification: string | null; amount: number }>;
    classes: Array<{ qbo_class_id: string; name: string | null; amount: number }>;
  };
  freshness: { last_synced_at: string | null };
}
export interface QboStatus {
  configured: boolean;
  connected: boolean;
  account_count: number;
  class_count: number;
  actuals_periods: number;
  last_synced_at: string | null;
  refresh_token_expires_at: string | null;
}
export interface QboAccountRow {
  qbo_account_id: string;
  name: string;
  account_type: string | null;
  classification: string | null;
  active: boolean;
  line_code: string | null;
  department_id: string | null;
  amount: number;  // trailing-12mo actuals, for $ materiality
}
export interface QboClassRow {
  qbo_class_id: string;
  name: string;
  active: boolean;
  department_id: string | null;
  amount: number;  // trailing-12mo actuals
}
export interface FpaDepartmentRow {
  id: string;
  name: string;
  division: string | null;
  active: boolean;
  sort_order: number;
}

// ─── FP&A — planning spine (Phase 3) ────────────────────────────────────────
export type PlanKind = 'budget' | 'forecast' | 'goal' | 'actual_py' | 'scenario';
export type PlanStatus = 'draft' | 'submitted' | 'approved' | 'locked' | 'superseded' | 'restated';
export type HeadcountStatus = 'planned' | 'filled' | 'open' | 'attrited';

export interface PlanVersion {
  id: string;
  name: string;
  kind: PlanKind;
  status: PlanStatus;
  horizon_start: string;
  horizon_end: string;
  parent_version_id: string | null;
  created_by: string;
  created_at: string;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  locked_at: string | null;
  actuals_through?: string | null;
  default_seasonality?: number[] | null;
  notes: string | null;
}
export interface PlanLockRow {
  plan_version_id: string;
  period_month: string;
  locked_by: string;
  locked_at: string;
  unlocked_at: string | null;
  unlocked_by: string | null;
  unlock_reason: string | null;
}
export interface PlanAssumptionRow {
  id: string;
  assumption_key: string;
  period_month: string | null;
  department_id: string | null;
  value_numeric: number | null;
  value_text: string | null;
}
export interface PlanFormulaRow {
  id: string;
  line_code: string;
  label: string;
  expression: string;
  scope: 'department' | 'company';
  sort_order: number;
}
export interface FormulaRefGroup {
  group: string;
  refs: Array<{ ref: string; label: string }>;
}
export type ModelNodeKind = 'driver' | 'revenue' | 'cost' | 'margin' | 'cash' | 'custom';
export interface ModelGraphNode {
  code: string;
  label: string;
  kind: ModelNodeKind;
  value: number | null;
  unit: 'ratio' | 'currency' | 'rate' | 'hours' | 'months';
  formula: string | null;
  depth: number;
  scope?: 'department' | 'company';
}
export interface ModelGraph {
  nodes: ModelGraphNode[];
  edges: Array<{ from: string; to: string }>;
}
export interface TmContribution { id: string; role_label: string; hours: number; util: number; ramp_productivity: number; rate: number; contribution: number }
export interface TmBuildRow { department_id: string; period_month: string; amount: number; sub_servicing_pct: number; contributions: TmContribution[] }
export interface AssumptionOverride { assumption_key: string; department_id: string | null; value_numeric: number | null }
export interface SavedScenarioRow { id: string; name: string; overrides: AssumptionOverride[]; created_at: string }
export interface PlanHeadcountRow {
  id: string;
  role_label: string;
  department_id: string;
  is_billable: boolean;
  status: HeadcountStatus;
  linked_user_id: string | null;
  start_month: string;
  end_month: string | null;
  annual_base_salary: number;
  fully_loaded_multiplier: number;
  target_utilisation: number | null;
  target_hours_per_head: number | null;
  target_bill_rate: number | null;
  ramp_curve_id: string | null;
  notes: string | null;
  linked_user_name: string | null;
}
export interface PlanLineRow {
  line_code: string;
  department_id: string | null;
  period_month: string;
  amount: number;
}
// ── Planning v2 ──
export type ItemMethod =
  | 'manual' | 'spread' | 'growth' | 'pct_of' | 'per_head' | 'formula'
  | 'engine_tm' | 'engine_salary' | 'engine_contract' | 'engine_pipeline' | 'engine_subscription' | 'actuals_seed';
export interface PlanItemRow {
  id: string;
  plan_version_id: string;
  line_code: string;
  label: string;
  department_id: string | null;
  method: ItemMethod;
  params: Record<string, unknown>;
  start_month: string | null;
  end_month: string | null;
  sign: 1 | -1;
  sort_order: number;
  is_active: boolean;
  source_ref: string | null;
  notes: string | null;
  updated_at: string;
  values?: Record<string, number>;
  overrides?: Record<string, { amount: number; reason: string | null }>;
  /** Per-month computed contribution (signed), attached by GET /plans/:id/items. */
  computed?: Record<string, number>;
}
export interface ItemUpsertInput {
  id?: string;
  line_code: string;
  label: string;
  department_id?: string | null;
  method: ItemMethod;
  params?: Record<string, unknown>;
  start_month?: string | null;
  end_month?: string | null;
  sign?: 1 | -1;
  sort_order?: number;
  is_active?: boolean;
  source_ref?: string | null;
  notes?: string | null;
  expected_updated_at?: string;
}
export interface ItemBatchInput {
  items?: ItemUpsertInput[];
  deleteItems?: string[];
  values?: Array<{ item_id: string; period_month: string; amount: number | null }>;
  overrides?: Array<{ item_id: string; period_month: string; amount: number | null; reason?: string | null }>;
}
export interface BlendedLineRow {
  line_code: string;
  department_id: string | null;
  period_month: string;
  amount: number;
  source: 'actual' | 'plan';
}
export interface CatalogLineRow {
  code: string;
  label: string;
  parent_code: string | null;
  family: 'revenue' | 'cogs' | 'salary' | 'opex' | 'other_income' | 'other_expense' | 'subtotal' | 'cash' | 'kpi';
  kind: 'leaf' | 'group' | 'computed';
  sort_order: number;
  sensitivity_tier: number;
  is_active: boolean;
}
export interface DeptSignal {
  department_id: string;
  active_heads: number;
  billable_hours: number;
  logged_hours: number;
  rev_tm: number;
  salary_monthly: number;
  bonus_annual: number;
  suggestions: { bill_rate: number | null; utilisation: number | null; hours_per_head: number | null; base_salary: number | null };
}
export interface PlanningSignals {
  window: { from: string; to: string; months: number };
  departments: DeptSignal[];
  company: { active_heads: number; salary_monthly: number; base_salary: number | null };
}
// ─── Predictive model (role economics / sales capacity / marginal) ───────────
export type EffRole = 'IC' | 'DM' | 'SA';
export interface RoleAggregate {
  role: EffRole; heads: number; heads_fte: number; heads_contractor: number;
  avg_shipped_value_per_month: number | null; avg_direct_billing_per_month: number | null;
  contractor_cost_per_month: number; avg_utilisation: number | null;
  avg_logged_hours_per_month: number | null; avg_ics_supported: number | null; avg_engagements: number | null;
}
export interface RolePersonEconomics {
  user_jira_id: string; display_name: string; role: EffRole; fpa_department_id: string | null;
  months_active: number; shipped_value_per_month: number | null; direct_billing_per_month: number | null;
  logged_hours_per_month: number | null; utilisation: number | null; avg_ics_supported: number | null; avg_engagements: number | null;
  is_contractor: boolean; contractor_cost_per_month: number | null;
}
export interface RoleEconomics {
  window: { from: string; to: string; months: number }; half_life_months: number;
  people: RolePersonEconomics[]; by_role: RoleAggregate[];
}
export interface SalesRep {
  hubspot_owner_id: string; owner_name: string | null; email: string | null; user_jira_id: string | null;
  archived: boolean;
  bookings_per_month: number | null; won_count: number; closed_count: number; win_rate: number | null;
  avg_deal_size: number | null; sales_cycle_days: number | null; open_pipeline_weighted: number;
}
export interface SalesCapacity {
  window: { from: string; to: string; months: number }; half_life_months: number; reps: SalesRep[];
  team: { active_reps: number; productive_reps: number; avg_bookings_per_rep: number | null;
    total_bookings_per_month: number | null; avg_win_rate: number | null; avg_deal_size: number | null;
    avg_sales_cycle_days: number | null; open_pipeline_weighted: number };
  expected_bookings_by_month: Array<{ month: string; expected: number; gross: number }>;
}
// ─── Sales performance dashboard ──────────────────────────────────────────────
export interface SalesMonthPoint { month: string; closed_won: number; won_count: number; target: number | null; }
export interface SalesQuotaSummary { target: number; actual: number; attainment: number | null; }
export interface SalesStageBucket {
  stage_id: string | null; label: string | null; probability: number | null;
  count: number; amount: number; weighted: number;
}
export interface SalesDealRow {
  deal_id: string; name: string | null; amount: number | null; weighted: number | null;
  close_date: string | null; stage_label: string | null; is_closed_won: boolean;
}
export interface SalesRepKpis {
  bookings_per_month: number | null; won_count: number; closed_count: number; win_rate: number | null;
  avg_deal_size: number | null; sales_cycle_days: number | null; open_pipeline_weighted: number;
}
export interface SalesRepDetail {
  hubspot_owner_id: string; owner_name: string | null; email: string | null;
  user_jira_id: string | null; archived: boolean;
  kpis: SalesRepKpis;
  quota: SalesQuotaSummary | null;
  trend: SalesMonthPoint[]; pipeline_by_stage: SalesStageBucket[];
  recent_won: SalesDealRow[]; open_deals: SalesDealRow[];
}
export interface SalesQuotaRep { hubspot_owner_id: string; owner_name: string | null; archived: boolean; }
export interface SalesQuotaRow { hubspot_owner_id: string; period_month: string; target_amount: number; }
export interface SalesQuotaGrid { year: number; reps: SalesQuotaRep[]; rows: SalesQuotaRow[]; }
export interface FiscalYearToDate { months: string[]; label: string; from: string; to: string; }
export interface SalesPerformance {
  window: { from: string; to: string; months: number };
  fiscal_year: FiscalYearToDate;
  half_life_months: number;
  is_manager: boolean;
  self_owner_id: string | null;
  team: {
    kpis: SalesCapacity['team'];
    quota: SalesQuotaSummary | null;
    trend: SalesMonthPoint[];
    pipeline_by_stage: SalesStageBucket[];
    expected_bookings_by_month: Array<{ month: string; expected: number; gross: number }>;
  };
  leaderboard: SalesRep[] | null;
  reps_directory: Array<{ hubspot_owner_id: string; owner_name: string | null; archived: boolean }> | null;
  rep: SalesRepDetail | null;
}
export type MarginalBinding = 'delivery' | 'sales' | 'balanced';
export interface MarginalPoint {
  supply: number; demand: number; revenue: number; binding: MarginalBinding;
  effective_ics: number; max_ics: number | null;
  cost: number; profit: number; margin: number | null;
}
export interface MarginalLever {
  role: 'sales' | 'IC' | 'DM' | 'SA';
  add: { delta_revenue: number; delta_profit: number; new_binding: MarginalBinding };
  subtract: { delta_revenue: number; delta_profit: number; new_binding: MarginalBinding };
}
interface MParamsShape {
  rev_per_ic: number; rev_per_dm: number; rev_per_sa: number; bookings_per_rep: number; uplift?: number;
  avg_ics_per_dm: number | null; avg_ics_per_sa: number | null; other_production_baseline?: number;
  fte_cost_per_head?: number; rep_cost_per_head?: number; contractor_base_cost?: number; contractor_heads?: number;
}
export interface MarginalModel {
  window: { from: string; to: string; months: number };
  params: MParamsShape;
  state: { ics: number; dms: number; sas: number; reps: number };
  baseline: MarginalPoint; levers: MarginalLever[];
}
export interface ForecastMonth {
  month: string; demand_in: number; demand: number; capacity: number;
  recognized: number; backlog: number; binding: 'capacity' | 'demand' | 'balanced';
}
export interface TimePhasedForecast {
  horizon: { from: string; to: string; months: number };
  params: { lag_months: number; duration_months: number; uplift: number; window_months: number };
  inputs: { capacity_per_month: number };
  series: ForecastMonth[];
}
export interface ModelInputs {
  window: { from: string; to: string; months: number };
  half_life_months: number;
  params: MParamsShape;
  base_state: { ics: number; dms: number; sas: number; reps: number };
  pipeline_by_month: Array<{ month: string; expected: number }>;
  won_by_month: Array<{ month: string; amount: number }>;
  defaults: { lag_months: number; duration_months: number; uplift: number; horizon_months: number };
}
export interface RampCurveRow {
  id: string;
  name: string;
  description: string | null;
  points: Array<{ month_offset: number; productivity: number }>;
}
export type CompEventType = 'merit' | 'promotion' | 'bonus_accrual' | 'equity_refresh' | 'one_time';
export interface PlanCompEventRow {
  id: string; plan_version_id: string; headcount_id: string | null; department_id: string | null;
  effective_month: string; event_type: CompEventType; amount_pct: number | null; amount_value: number | null; notes: string | null;
}

// ─── FP&A — variance (Phase 4) ──────────────────────────────────────────────
export type FpaVarianceCategory =
  | 'revenue' | 'cost.variable' | 'cost.salary' | 'cost.fixed' | 'gross_margin' | 'operating_profit';
export interface VarianceBaselineMeta { id: string; name: string; kind: string; status: string }
export interface VarianceCell { period_month: string; actual: number; baselines: Record<string, number> }
export interface VarianceDeptRow {
  department_id: string;
  cells: VarianceCell[];
  total: { actual: number; baselines: Record<string, number> };
}
export interface VarianceCategoryRow {
  category: FpaVarianceCategory;
  cells: VarianceCell[];
  total: { actual: number; baselines: Record<string, number> };
  by_department: VarianceDeptRow[];
}
export interface VarianceReport {
  from: string; to: string; periods: string[];
  baselines: VarianceBaselineMeta[];
  rows: VarianceCategoryRow[];
  freshness: { last_synced_at: string | null };
}
// ─── Unified statement (one contract behind Plan P&L / Actuals / Variance) ────
export type StatementVersionKind = 'actual' | 'budget' | 'forecast' | 'goal' | 'plan';
export interface StatementVersion { key: string; label: string; kind: StatementVersionKind; status?: string }
export interface StatementCell { period_month: string; values: Record<string, number> }
export interface StatementDeptRow { department_id: string; cells: StatementCell[]; total: Record<string, number> }
export interface StatementRow { line_code: string; cells: StatementCell[]; total: Record<string, number>; by_department: StatementDeptRow[] }
export interface FpaStatement {
  from: string; to: string; periods: string[];
  versions: StatementVersion[];
  rows: StatementRow[];
  freshness: { last_synced_at: string | null };
}
export interface VarianceContribution {
  side: 'actual' | 'plan'; key: string; label: string;
  line_code: string; department_id: string | null; amount: number; lineage?: unknown;
}
export interface ContributionsResult {
  category: string; period_month: string;
  actual: VarianceContribution[]; plan: VarianceContribution[];
}
export interface FpaVarianceNote {
  id: string; plan_version_id: string | null; category: string;
  department_id: string | null; period_month: string | null;
  period_grain: 'month' | 'quarter' | 'fy'; author_id: string; body: string;
  created_at: string; edited_at: string | null;
}

// ─── FP&A — revenue depth (Phase 5) ─────────────────────────────────────────
export type PlanRevenueType = 'FF' | 'SUB';
export interface PlanRevenueContractRow {
  id: string; plan_version_id: string; revenue_type: PlanRevenueType; label: string;
  department_id: string; value: number; start_month: string; duration_months: number;
  source_engagement_id: string | null; notes: string | null;
}
export interface EngagementRevtypeRow { engagement_type: string; revenue_type: 'TM' | 'FF' | 'SUB' }

// ─── FP&A — close & monitoring (Phase 6) ────────────────────────────────────
export interface CloseCalendarStep {
  id: string; period_month: string; step_code: string; step_name: string;
  owner_role: string | null; target_date: string | null;
  completed_at: string | null; completed_by: string | null; notes: string | null;
}
export interface CloseChecklistItem {
  id: string; period_month: string; task_name: string; assignee_id: string | null;
  due_date: string | null; completed_at: string | null; completed_by: string | null; notes: string | null;
}
export type GlReconStatus = 'matched' | 'unexplained' | 'reconciled';
export interface GlReconRow {
  id: string; period_month: string; line_code: string;
  fpa_amount: number; gl_amount: number; delta: number; status: GlReconStatus;
  reconciling_items: Array<{ amount: number; description: string }> | null;
  signed_off_by: string | null; signed_off_at: string | null;
}
export interface PlanQualityRow {
  plan_version_id: string; line_code: string; department_id: string | null; period_month: string;
  forecast_value: number; actual_value: number; abs_pct_error: number; signed_pct_error: number;
}
export interface PlanQualitySummary {
  rows: PlanQualityRow[];
  overall: { mape: number; bias: number; count: number };
  by_category: Array<{ category: string; mape: number; bias: number; count: number }>;
}

// ─── FP&A — executive dashboards (Phase 8) ──────────────────────────────────
export type DashboardMetricFormat = 'currency' | 'percent' | 'months' | 'count' | 'days' | 'ratio' | 'list';
export interface DashboardCard {
  code: string;
  display_name: string;
  requires_fpa_role: boolean;
  drilldown_target: string | null;
  display_order: number;
  hidden: boolean;
  value: number | string | null;
  format: DashboardMetricFormat;
  sub?: string | null;
  placeholder?: string;
}

export interface User {
  jira_user_id: string;
  display_name: string;
  email: string;
  role: Role;
  /** Player/coach override for bonus calc. E.g. role=DIRECTOR + bonus_role=DM. */
  bonus_role: BonusRole | null;
  title: string;
  team: string;
  manager_jira_user_id: string | null;
  is_admin: boolean;
  /** Gates access to the ChargeOver billing workflow. Admins implicitly
   *  have access too — the middleware on the server accepts either flag. */
  is_billing_admin: boolean;
  /** FP&A capability grant (viewer/analyst/approver). NULL = no FP&A access.
   *  Unlike is_billing_admin, is_admin does NOT imply this — FP&A access is an
   *  explicit grant only. */
  fpa_role: FpaRole | null;
  /** FP&A department membership (fpa_departments.id). NULL when unmapped. */
  fpa_department_id?: string | null;
  /** Billed contractor: variable cost, not bonus-eligible. */
  is_contractor?: boolean;
  contractor_cost_ratio?: number | null;
  is_active: boolean;
  /** Maps this ORP user to the matching ChargeOver admin (sales rep).
   *  Auto-populated from email match at sync time; can be manually
   *  overridden via the Users page dropdown. NULL when unmapped. */
  chargeover_user_id: string | null;
  /** Derived: linked to a HubSpot owner who owns deals ("in sales"). Set by
   *  /api/auth/me; gates the Sales dashboard. Absent/false for non-sales users. */
  is_sales?: boolean;
  /** Effective permission keys (role-default profile + assigned profiles +
   *  legacy flags), resolved server-side. Set by /api/auth/me; the app gates
   *  nav/routes on these, mirroring the API gates. FP&A is NOT covered here —
   *  it stays on fpa_role. */
  permissions?: string[];
  /** Additional permission profiles assigned to this user (profile ids).
   *  Returned by the admin users list; edited via users.update. */
  permission_profile_ids?: string[];
}

// ─── Permission profiles ─────────────────────────────────────────────────────

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  /** UI grouping on the Permission profiles page. */
  category: string;
  /** True when grants of this key carry a data-visibility scope. */
  scoped?: boolean;
}

/** Data-visibility level a grant of a scoped permission carries. */
export type PermissionScope = 'self' | 'direct_reports' | 'subtree' | 'all';

export interface ProfileGrant {
  key: string;
  scope: PermissionScope;
}

export interface PermissionProfile {
  id: string;
  name: string;
  description: string | null;
  /** Non-null on seeded role defaults (auto-applied by users.role; editable,
   *  not deletable/renameable). */
  system_role: string | null;
  permissions: ProfileGrant[];
  /** Users carrying this profile as an ADDITIONAL assignment (role defaults
   *  apply implicitly and don't count here). */
  member_count: number;
}

export interface ChargeoverAdmin {
  chargeover_admin_id: string;
  email: string | null;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  last_login_at: string | null;
}

export interface HubspotPipelineLite {
  hubspot_pipeline_id: string;
  label: string | null;
  display_order: number | null;
  /** Total deals on this pipeline. Surfaced in the settings UI so the
   *  operator can see at a glance which pipelines actually carry data. */
  deal_count: number;
  /** Non-closed deals (forecast / open) on this pipeline. */
  open_count: number;
}

// ─── Sales overlay ───────────────────────────────────────────────────────────

export interface SalesOverlayMonth {
  month: string;               // YYYY-MM
  shipped: number;             // realised delivery value (attribution_month buckets)
  wip: number;                 // in-flight delivery value (by due_date)
  closed_won: number;          // HubSpot deals with is_closed_won (by close_date)
  forecast: number;            // open deals × stage prob OR weighted_amount property
  /** Same forecast bucket but unweighted (gross deal amounts). Surfaced
   *  as a secondary line / tooltip so the operator can sanity-check what
   *  the weighting is doing. */
  forecast_unweighted: number;
  /** WIP accumulation — value of work actually DONE in the period (worklog
   *  work_date buckets). Overlays the chart as a line so the operator can
   *  compare "work landing now" vs "shipped now" — a months-long divergence
   *  flags burn-ahead-of-attribution. */
  wip_done: number;
  /** ChargeOver invoice activity for the month (invoice_date, issued only;
   *  drafts + voids excluded). All three are summed across the same invoice
   *  set so they can be stacked meaningfully in the chart. */
  invoice_deposit: number;
  invoice_billed: number;
  invoice_paid: number;
  /** Distinct authors with billable worklog time in the month. Right-axis
   *  line overlay; null/zero on months with no billable activity. */
  active_employees: number;
  /** Distinct parent engagements (the layer above deliverables) with
   *  billable worklog time in the month. Same right-axis count scale as
   *  active_employees — "how many projects were in flight". */
  active_engagements: number;
  /** Distinct parent engagements with at least one deliverable attributed
   *  to the month (i.e. shipped). Different from active_engagements,
   *  which counts worklog activity — an engagement with ongoing burn
   *  but no completion in the month counts there but not here. */
  engagements_shipped: number;
  /** Distinct closed-won HubSpot deals in the month. Filtered by the
   *  `hubspot_deal_count_pipelines` setting independently of the
   *  dollar-value series — so the count can be narrowed to e.g. the
   *  Implementation pipeline while the $ totals stay broad. */
  deals_closed_won: number;
  /** Distinct engagements that were open (started, not yet shipped) during
   *  the month. Computed from Start Date + ship date (actual completion, or
   *  Planned Ship Date for not-yet-shipped engagements). */
  open_engagements: number;
  /** Mean age (weeks since Start Date) of those open engagements, measured at
   *  month end. 1-decimal. 0 when no engagements were open that month. */
  avg_open_age_weeks: number;
}

export interface SalesOverlayReport {
  from_month: string;
  to_month: string;
  current_month: string;
  /** Engagement types available to filter on, grouped by their cascading
   *  parent. Each group's `items` are the leaf values (sub-items); a
   *  parent-only option appears as a group whose single item equals the
   *  group name. */
  available_engagement_types: Array<{ group: string; items: string[] }>;
  /** Engagement types (leaf values) currently applied as a filter (echo of
   *  the request). */
  engagement_types: string[];
  months: SalesOverlayMonth[];
  /** All totals are scoped to the selected [from_month, to_month] range.
   *  Dollar fields are summed across the range; count fields are DISTINCT
   *  over the whole range (not a sum of per-month counts). */
  totals: {
    shipped: number;
    wip: number;
    wip_done: number;
    closed_won: number;
    forecast: number;
    forecast_unweighted: number;
    deposit: number;
    billed: number;
    paid: number;
    active_employees: number;
    active_engagements: number;
    engagements_shipped: number;
    deals_closed_won: number;
    /** Distinct engagements open (started, not yet shipped) at any point in
     *  the range. */
    open_engagements: number;
    /** Mean age (weeks since Start Date) of engagements open as of the END of
     *  the selected range. */
    avg_open_age_weeks: number;
  };
}

export interface BonusTierConfig {
  role: Role;
  tier: number;
  threshold_amount: number;
  multiplier: number;
  effective_from: string;
}

export interface BonusTierOverride {
  user_jira_id: string;
  tier: number;
  threshold_amount: number;
  multiplier: number;
  effective_from: string;
}

export interface TierRow {
  tier: number;
  threshold_amount: number;
  multiplier: number;
}

export interface YearRoleEntry {
  role: Role;
  /** ISO date of the version active at year-start, or null if no version exists yet. */
  effective_from: string | null;
  /** True when the active version comes from a previous year (carry-forward). */
  is_inherited: boolean;
  tiers: TierRow[];
}

export interface YearOverrideEntry {
  user: { jira_user_id: string; display_name: string; email: string; role: Role; title: string; team: string };
  effective_from: string;
  /** True when the override version's effective_from falls inside the requested year. */
  is_in_year: boolean;
  tiers: TierRow[];
}

export interface TierYearSummary {
  year: number;
  year_start: string;
  roles: YearRoleEntry[];
  overrides: YearOverrideEntry[];
}

// ─── Sales commission rate schedule ──────────────────────────────────────────
// rate_pct is a PERCENT (8.5 = 8.5%). A deal reads the version in force on its
// CLOSE DATE and keeps it for life, so editing a later year never re-prices a
// deal that already closed.

/** The catch-all deal type: covers types with no row of their own and deals
 *  whose HubSpot dealtype is empty. */
export const CATCH_ALL_DEAL_TYPE = '*';

export interface CommissionRateRow {
  deal_type: string;
  rate_pct: number;
  duration_months: number;
}

export interface CommissionRateEntry extends CommissionRateRow {
  effective_from: string;
  /** True when the active version comes from an earlier year (carry-forward). */
  is_inherited: boolean;
}

export interface CommissionOverrideInput {
  deal_type: string;
  rate_pct: number;
  /** null = inherit the payout window from the base schedule. */
  duration_months: number | null;
}

export interface CommissionOverrideRow extends CommissionOverrideInput {
  hubspot_owner_id: string;
  effective_from: string;
}

export interface CommissionDealType {
  value: string;
  label: string;
  /** Closed-won deals carrying this type — helps spot which rows matter. */
  deal_count: number;
}

export interface CommissionRep {
  hubspot_owner_id: string;
  owner_name: string | null;
  archived: boolean;
}

/** One participant's share of a deal, mirrored from HubSpot. Used both by the
 *  commission engine and by the deal-onboarding record. */
export interface DealSplitShare {
  hubspot_owner_id: string;
  owner_name: string | null;
  /** 0..1 — a fraction, matching how HubSpot stores it. */
  split_percentage: number;
}

export interface CommissionPayoutRow {
  chargeover_invoice_id: string;
  invoice_number: string | null;
  hubspot_owner_id: string;
  owner_name: string | null;
  owner_email: string | null;
  hubspot_deal_id: string;
  deal_name: string | null;
  deal_type: string | null;
  account_name: string | null;
  tempo_account_id: string | null;
  billed_date: string;
  deal_close_date: string;
  window_end_date: string;
  /** Services billed, before the deposit drawdown was netted off. */
  gross_services_amount: number;
  /** Deposit credit applied to this invoice. That money earned commission when
   *  the deposit was invoiced, so it is netted out of this payout. */
  deposit_credit_amount: number;
  commissionable_amount: number;
  split_percentage: number;
  rate_pct: number;
  rate_source: 'base' | 'override';
  commission_amount: number;
}

export interface CommissionRepTotal {
  hubspot_owner_id: string;
  owner_name: string | null;
  owner_email: string | null;
  invoice_count: number;
  commissionable_amount: number;
  /** This rep's share of the deposit drawdown netted off their invoices. */
  deposit_credit_amount: number;
  commission_amount: number;
}

export interface CommissionPaymentRun {
  month: string;
  status: CommissionPeriodStatus;
  reps: CommissionRepTotal[];
  rows: CommissionPayoutRow[];
  total_commission: number;
  total_commissionable: number;
  /** Deposit drawdown netted out of the month, counted once per invoice. */
  total_deposit_credit: number;
}

export interface CommissionCollectionRow {
  chargeover_invoice_id: string;
  invoice_number: string | null;
  account_name: string | null;
  billed_date: string;
  invoice_total: number;
  commissionable_amount: number;
  commission_amount: number;
  collected_amount: number;
  /** Credit memos from a paid deposit invoice — not new cash. */
  deposit_credit_amount: number;
  collected_ratio: number;
  commission_collected: number;
  last_payment_date: string | null;
}

export interface CommissionCollections {
  from: string;
  to: string;
  invoices: CommissionCollectionRow[];
  reps: Array<{
    hubspot_owner_id: string;
    owner_name: string | null;
    commission_amount: number;
    commission_collected: number;
  }>;
  totals: {
    commissionable_amount: number;
    commission_amount: number;
    collected_amount: number;
    commission_collected: number;
    invoice_total: number;
  };
}

export interface RepCommissionMonth {
  period_month: string;
  status: CommissionPeriodStatus;
  commission_amount: number;
  commissionable_amount: number;
  invoice_count: number;
}

export interface RepCommissionSummary {
  hubspot_owner_id: string | null;
  months: RepCommissionMonth[];
  recent: CommissionPayoutRow[];
  total_paid: number;
  total_pending: number;
  is_manager: boolean;
  is_self: boolean;
}

export type CommissionExceptionKind =
  | 'ambiguous_account'
  | 'deposit_credit_after_lock'
  | 'deposit_credit_unverified'
  | 'missing_services_amount'
  | 'no_rate'
  | 'no_owner'
  | 'split_sum_mismatch';

export interface CommissionException {
  kind: CommissionExceptionKind;
  /** The account key, invoice id or deal id to go and fix. */
  subject: string;
  detail: string;
  /** Money that is wrong because of this, where we can tell — always positive.
   *  `direction` says which way. */
  amount_at_risk: number | null;
  /** 'unpaid' (the default when absent) = commission we aren't paying out.
   *  'overpaid' = commission already paid that shouldn't have been. Never sum
   *  the two into one figure. */
  direction?: 'unpaid' | 'overpaid';
}

export type CommissionPeriodStatus = 'open' | 'locked' | 'approved' | 'paid';

export interface CommissionPeriodRow {
  period_month: string;
  status: CommissionPeriodStatus;
  row_count: number;
  rep_count: number;
  total_commission: number;
  /** Invoiced services behind the payouts — each invoice counted once, even
   *  when a split spreads it across several reps. */
  total_commissionable: number;
  locked_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
}

export interface CommissionRecomputeResult {
  rows_written: number;
  periods_written: number;
  periods_locked: number;
  exceptions: number;
  skipped: Record<string, number>;
}

export interface CommissionBackfillResult {
  scanned: number;
  updated: number;
  unchanged: number;
  errored: number;
  remaining: number;
}

export interface CommissionRateYear {
  year: number;
  year_start: string;
  deal_types: CommissionDealType[];
  rates: CommissionRateEntry[];
  overrides: Array<{
    hubspot_owner_id: string;
    owner_name: string | null;
    rates: Array<CommissionOverrideInput & { effective_from: string; is_inherited: boolean }>;
  }>;
  reps: CommissionRep[];
}

export interface ScorecardPeriod {
  attribution_month: string;
  shipped_value: number;
  shipped_value_deliverables?: number;
  shipped_value_overheads?: number;
  wip_value: number;
  /** Forfeited value from Canceled deliverables/overheads. Separate from
   *  both Shipped and WIP so drops in the other buckets are traceable. */
  canceled_value: number;
  bonus_tier: number | null;
  bonus_multiplier: number | null;
  gross_bonus: number | null;
  /** Clawback applied in this period (spec §4.3). Zero if none. */
  clawback_applied?: number;
  /** gross_bonus − clawback_applied. The payable amount. */
  net_bonus?: number | null;
  is_locked: boolean;
  /** Average shipped_value across peers with the same effective bonus role in
   *  this month — "your peers who shipped anything averaged $X". Null for
   *  rollup-only directors/execs. */
  role_avg_shipped_value?: number | null;
}

export interface Clawback {
  id: string;
  user_jira_id: string;
  original_attribution_month: string;
  work_item_jira_issue_id: string;
  written_off_billable_hours: number;
  clawback_amount: number;
  applied_to_attribution_month: string | null;
  created_by_admin_user_id: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScorecardResponse {
  user: User;
  /**
   * For bonus-eligible users (incl. player/coach directors with a bonus_role)
   * these are the user's personal scorecard periods. For pure directors/execs
   * this is the subtree rollup (same as team_rollup.periods) — check is_rollup.
   */
  periods: ScorecardPeriod[];
  tier_config: BonusTierConfig[];
  /** True only when periods IS the rollup (no personal bonus role). */
  is_rollup: boolean;
  /** The user's effective bonus role, null for pure directors/execs. */
  bonus_role: BonusRole | null;
  /** Subtree rollup — always present for directors/execs, even player/coach. */
  team_rollup: {
    periods: ScorecardPeriod[];
    member_count: number;
    total_wip_value: number;
    total_canceled_value?: number;
    /** Each descendant's per-month contribution. Used by the rollup drill-down
     *  modal to render a per-member breakdown for the clicked period. */
    members: Array<{
      user: User;
      periods: ScorecardPeriod[];
      total_wip_value: number;
      total_canceled_value?: number;
    }>;
  } | null;
}

export type IssueType = 'Deliverable' | 'Overhead';

export interface DeliverableRow {
  jira_issue_id: string;
  jira_issue_key: string;
  issue_type: IssueType;
  summary: string;
  status: string;
  attribution_month: string | null;
  tempo_account_id: string;
  tempo_account_name: string;
  billable_rate: number;
  my_billable_hours: number;
  total_billable_hours: number;
  shipped_value: number;
  delivery_manager_display_name: string | null;
  solution_architect_display_name: string | null;
  parent_engagement_summary: string | null;
}

export interface WeeklyScorecardRow {
  /** Populated only in rollup mode (DIRECTOR/EXEC); null otherwise. */
  member_jira_user_id: string | null;
  /** Populated only in rollup mode. */
  member_display_name: string | null;
  deliverable_jira_issue_id: string;
  jira_issue_key: string;
  issue_type: IssueType;
  deliverable_summary: string;
  deliverable_status: string;
  engagement_jira_issue_id: string | null;
  engagement_summary: string | null;
  billable_rate: number;
  week_start: string; // YYYY-MM-DD (Monday)
  hours: number;
  value: number;
}

export interface WeeklyScorecardResponse {
  user: User;
  /** True when the response aggregates a subtree (director/exec). */
  is_rollup?: boolean;
  week_starts: string[]; // chronological Mondays, oldest → newest
  rows: WeeklyScorecardRow[];
}

// ─── Gantt ───────────────────────────────────────────────────────────────────

export interface GanttWorklog {
  work_date: string;
  hours: number;
  value: number;
  author_jira_user_id: string;
  author_display_name: string;
  author_team: string;
  /** True when the author's user row is still active. Deactivated authors
   *  with billable time in the window still surface on the timeline — the
   *  UI tags them so they're not mistaken for current staff. */
  author_is_active: boolean;
  deliverable_jira_issue_id: string;
  deliverable_jira_issue_key: string;
  deliverable_summary: string;
  issue_type: IssueType;
  /** The Jira issue the worklog was logged against — usually a Package/Task
   *  under the parent Deliverable, but equals deliverable_jira_issue_id when
   *  logged directly on the parent. */
  source_issue_jira_issue_id: string;
  engagement_jira_issue_id: string | null;
  engagement_summary: string | null;
}

export interface GanttChildIssue {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string;
  status: string;
  /** 'Task' | 'Package' (raw Jira issue type). */
  issue_type: string;
  parent_deliverable_jira_issue_id: string;
}

export interface GanttDeliverable {
  jira_issue_id: string;
  jira_issue_key: string;
  issue_type: IssueType;
  summary: string;
  status: string;
  completed_date: string | null;
  due_date: string | null;            // "get it to the client by" deadline
  planned_ship_date: string | null;   // when we plan to actually ship
  start_target: string | null;      // planned kickoff
  start_actual: string | null;      // actual kickoff
  engagement_jira_issue_id: string | null;
  engagement_summary: string | null;
  delivery_manager_jira_user_id: string | null;
  solution_architect_jira_user_id: string | null;
}

export interface GanttEngagement {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string;
  completed_date: string | null;
  planned_ship_date: string | null;
  start_target: string | null;
  start_actual: string | null;
}

export interface GanttResponse {
  mode: 'staff' | 'team' | 'engagement';
  subject: { id: string; label: string };
  from: string;
  to: string;
  worklogs: GanttWorklog[];
  deliverables: GanttDeliverable[];
  children: GanttChildIssue[];
  engagements: GanttEngagement[];
}

export interface GanttSubjectsResponse {
  teams: Array<{ name: string; member_count: number }>;
  engagements: Array<{
    jira_issue_id: string;
    jira_issue_key: string;
    summary: string;
    completed_date: string | null;
  }>;
}

export interface OrgPeriodSummary {
  attribution_month: string;
  total_shipped_value: number;
  total_wip_value: number;
  is_locked: boolean;
  employee_count: number;
  employees_on_tier: number;
}

export interface RefreshRun {
  id: string;
  triggered_by: 'scheduler' | 'manual';
  started_at: string;
  ended_at: string | null;
  status: 'running' | 'success' | 'warning' | 'failed';
  deliverables_processed: number;
  worklogs_processed: number;
  error_message: string | null;
  is_full_sync: boolean;
}

export interface RefreshWarning {
  id: string;
  run_id: string;
  /** 'missing_customer' | 'missing_item' | 'fixed_fee' | 'orphan_worklog' | ... */
  category: string;
  subject: string | null;
  message: string;
  created_at: string;
}

// ─── API calls ─────────────────────────────────────────────────────────────

export interface TeamMemberSummary {
  user: User;
  periods: ScorecardPeriod[];
  /** Cumulative WIP across all unshipped deliverables — not attribution-bucketed. */
  total_wip_value: number;
}

export interface TeamRollupRow {
  attribution_month: string;
  total_shipped_value: number;
  total_wip_value: number;
  member_count: number;
}

export interface TeamSubtreeSummary {
  root: User;
  member_count: number;
  members: TeamMemberSummary[];
  rollup: TeamRollupRow[];
  total_wip_value: number;
}

export interface AppConfig {
  jira_base_url: string | null;
  jira_browse_url: string | null;
  /** ChargeOver admin-UI base URL (e.g. https://billing.example.com). Used
   *  to build per-invoice links. Null when CHARGEOVER_ADMIN_BASE_URL isn't
   *  configured — UI falls back to rendering the invoice id as plain text. */
  chargeover_admin_base_url: string | null;
  /** Full template with a `{id}` placeholder. Pre-built server-side so the
   *  client doesn't have to re-encode the convention. */
  chargeover_invoice_url_template: string | null;
  /** HubSpot deal-link template with a `{id}` placeholder (deal id). Null when
   *  HUBSPOT_PORTAL_ID isn't configured — deal ids render as plain text. */
  hubspot_deal_url_template: string | null;
}

export const api = {
  config: () => request<AppConfig>('/config'),
  auth: {
    me: () => request<{ user: User }>('/auth/me'),
  },
  dashboards: {
    me: () => request<{ cards: DashboardCard[] }>('/dashboards/me'),
    update: (updates: Array<{ metric_code: string; hidden?: boolean; display_order?: number }>) =>
      request<{ cards: DashboardCard[] }>('/dashboards/me', { method: 'PATCH', body: JSON.stringify({ updates }) }),
  },
  team: {
    summary: (params?: { userId?: string; months?: number }) => {
      const qs = new URLSearchParams();
      if (params?.userId) qs.set('userId', params.userId);
      if (params?.months) qs.set('months', String(params.months));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<TeamSubtreeSummary>(`/team/summary${suffix}`);
    },
    members: () => request<{ members: User[] }>(`/team/members`),
  },
  scorecard: {
    get: (userId: string, months = 6) =>
      request<ScorecardResponse>(`/scorecard/${userId}?months=${months}`),
    deliverables: (userId: string, params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<{ user: User; deliverables: DeliverableRow[] }>(
        `/scorecard/${userId}/deliverables${qs}`,
      );
    },
    weekly: (userId: string, opts: { weeks?: number; kind?: 'shipped' | 'wip' | 'all' } = {}) => {
      const qs = new URLSearchParams();
      qs.set('weeks', String(opts.weeks ?? 8));
      if (opts.kind && opts.kind !== 'all') qs.set('kind', opts.kind);
      return request<WeeklyScorecardResponse>(`/scorecard/${userId}/weekly?${qs.toString()}`);
    },
  },
  gantt: {
    get: (opts: { mode: 'staff' | 'team' | 'engagement'; id: string; from?: string; to?: string }) => {
      const qs = new URLSearchParams({ mode: opts.mode, id: opts.id });
      if (opts.from) qs.set('from', opts.from);
      if (opts.to)   qs.set('to',   opts.to);
      return request<GanttResponse>(`/gantt?${qs.toString()}`);
    },
    subjects: () => request<GanttSubjectsResponse>(`/gantt/subjects`),
  },
  admin: {
    users: {
      list: () => request<{ users: User[] }>('/admin/users'),
      update: (userId: string, data: Partial<User>) =>
        request<{ user: User }>(`/admin/users/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),
    },
    permissionProfiles: {
      list: () => request<{ permissions: PermissionDef[]; scopes: PermissionScope[]; profiles: PermissionProfile[] }>('/admin/permission-profiles'),
      create: (data: { name: string; description?: string | null; permissions: ProfileGrant[] }) =>
        request<{ id: string }>('/admin/permission-profiles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: { name?: string; description?: string | null; permissions?: ProfileGrant[] }) =>
        request<{ ok: boolean }>(`/admin/permission-profiles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      remove: (id: string) =>
        request<{ ok: boolean }>(`/admin/permission-profiles/${id}`, { method: 'DELETE' }),
    },
    chargeoverAdmins: {
      list: () => request<{ admins: ChargeoverAdmin[] }>('/admin/chargeover-admins'),
    },
    fpaDepartments: {
      list: () => request<{ departments: Array<{ id: string; name: string; division: string | null }> }>('/admin/fpa-departments'),
    },
    hubspotPipelines: {
      list: () => request<{ pipelines: HubspotPipelineLite[] }>('/admin/hubspot-pipelines'),
    },
    orgSummary: () => request<{
      periods: OrgPeriodSummary[];
      current_month: string | null;
      current_users: Array<{
        jira_user_id: string;
        display_name: string;
        role: Role;
        team: string;
        shipped_value: number;
        bonus_tier: number | null;
      }>;
      active_user_count: number;
      active_engagement_count: number;
    }>('/admin/org-summary'),
    periods: {
      list: () => request<{ periods: PeriodRow[] }>('/admin/periods'),
      lock: (month: string) =>
        request<{ locked: boolean; month: string; lockedCount: number }>(
          `/admin/periods/${month}/lock`,
          { method: 'POST' },
        ),
      approve: (month: string) =>
        request<{ approved: boolean; month: string; approvedCount: number }>(
          `/admin/periods/${month}/approve`,
          { method: 'POST' },
        ),
      markPaid: (month: string) =>
        request<{ paid: boolean; month: string; paidCount: number; paidAt: string }>(
          `/admin/periods/${month}/mark-paid`,
          { method: 'POST' },
        ),
    },
    forecast: (months = 3) => request<ForecastResponse>(`/admin/reports/forecast?months=${months}`),
    salesOverlay: (fromMonth?: string, toMonth?: string, engagementTypes?: string[]) => {
      const qs = new URLSearchParams();
      if (fromMonth) qs.set('from', fromMonth);
      if (toMonth)   qs.set('to', toMonth);
      if (engagementTypes && engagementTypes.length) qs.set('engagement_types', engagementTypes.join(','));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<SalesOverlayReport>(`/admin/reports/sales-overlay${suffix}`);
    },
    refreshRuns: {
      list: () => request<{ runs: RefreshRun[] }>('/admin/refresh-runs'),
      // 202 + runId on success; throws on 409 when a sync is already running.
      trigger: () =>
        request<{ message: string; runId: string }>('/admin/refresh-runs', { method: 'POST' }),
      warnings: (runId: string) =>
        request<{ warnings: RefreshWarning[] }>(`/admin/refresh-runs/${encodeURIComponent(runId)}/warnings`),
    },
    writebacks: {
      summary: () => request<{
        pending: number;
        failed_latest: Array<{ jira_issue_key: string; issue_type: string; last_error: string | null; attempt_count: number }>;
        done_today: number;
      }>('/admin/writebacks/summary'),
      list: (state: 'pending' | 'failed' | 'done' | 'all' = 'pending') =>
        request<{ writebacks: WritebackDetailRow[] }>(`/admin/writebacks?state=${state}`),
      drain: () => request<{
        attempted: number;
        succeeded: number;
        failed: number;
        skipped_unconfigured: number;
      }>('/admin/writebacks/drain', { method: 'POST' }),
    },
    refreshDepositFlags: (limit = 500) =>
      request<{
        scanned: number;
        updated: number;
        unchanged: number;
        errored: number;
        remaining: number;
      }>('/admin/billing/refresh-deposit-flags', {
        method: 'POST',
        body: JSON.stringify({ limit }),
      }),
    webhooks: {
      chargeover: (limit = 200) =>
        request<{ webhooks: WebhookReceiptRow[] }>(`/admin/webhooks/chargeover?limit=${limit}`),
    },
    /** Invoices whose ChargeOver webhook never arrived, and the repair for
     *  them. `preview` is a dry run: it decides against live ChargeOver
     *  records but writes nothing. */
    webhookCatchup: {
      pending: (limit = 100) =>
        request<{
          counts: CatchupCounts;
          candidates: CatchupCandidate[];
        }>(`/admin/billing/webhook-catchup?limit=${limit}`),
      run: (opts: { limit?: number; dryRun: boolean; actions?: CatchupAction[] }) =>
        request<CatchupRunResult>('/admin/billing/webhook-catchup', {
          method: 'POST',
          body: JSON.stringify({
            limit: opts.limit ?? 50,
            dry_run: opts.dryRun,
            ...(opts.actions ? { actions: opts.actions } : {}),
          }),
        }),
    },
    tierYearSummary: (year: number) =>
      request<TierYearSummary>(`/admin/tiers/year-summary?year=${year}`),
    tierConfig: {
      list: () => request<{ tiers: BonusTierConfig[] }>('/admin/tier-config'),
      save: (role: Role, effectiveFrom: string, tiers: TierRow[]) =>
        request<{ saved: true; tier_count: number }>(
          `/admin/tier-config/${role}/${effectiveFrom}`,
          { method: 'PUT', body: JSON.stringify({ tiers }) },
        ),
      delete: (role: Role, effectiveFrom: string) =>
        request<{ deleted: true }>(
          `/admin/tier-config/${role}/${effectiveFrom}`,
          { method: 'DELETE' },
        ),
    },
    tierOverrides: {
      list: (userId: string) =>
        request<{ overrides: BonusTierOverride[] }>(`/admin/users/${userId}/tier-overrides`),
      save: (userId: string, effectiveFrom: string, tiers: TierRow[]) =>
        request<{ saved: true; tier_count: number }>(
          `/admin/users/${userId}/tier-overrides/${effectiveFrom}`,
          { method: 'PUT', body: JSON.stringify({ tiers }) },
        ),
      delete: (userId: string, effectiveFrom: string) =>
        request<{ deleted: true }>(
          `/admin/users/${userId}/tier-overrides/${effectiveFrom}`,
          { method: 'DELETE' },
        ),
    },
    commissionRates: {
      year: (year: number) => request<CommissionRateYear>(`/admin/commission-rates?year=${year}`),
      save: (effectiveFrom: string, rates: CommissionRateRow[]) =>
        request<{ saved: number; effective_from: string }>(
          `/admin/commission-rates/${effectiveFrom}`,
          { method: 'PUT', body: JSON.stringify({ rates }) },
        ),
      delete: (effectiveFrom: string) =>
        request<{ deleted: number }>(`/admin/commission-rates/${effectiveFrom}`, { method: 'DELETE' }),
      overrides: (ownerId: string) =>
        request<{ overrides: CommissionOverrideRow[] }>(
          `/admin/commission-rates/overrides/${encodeURIComponent(ownerId)}`,
        ),
      saveOverrides: (ownerId: string, effectiveFrom: string, rates: CommissionOverrideInput[]) =>
        request<{ saved: number }>(
          `/admin/commission-rates/overrides/${encodeURIComponent(ownerId)}/${effectiveFrom}`,
          { method: 'PUT', body: JSON.stringify({ rates }) },
        ),
      deleteOverrides: (ownerId: string, effectiveFrom: string) =>
        request<{ deleted: number }>(
          `/admin/commission-rates/overrides/${encodeURIComponent(ownerId)}/${effectiveFrom}`,
          { method: 'DELETE' },
        ),
    },
    commissionPeriods: {
      list: () => request<{ periods: CommissionPeriodRow[] }>('/admin/commission-periods'),
      lock: (month: string) =>
        request<{ locked: true; rows: number; total_commission: number }>(
          `/admin/commission-periods/${month}/lock`, { method: 'POST' }),
      approve: (month: string) =>
        request<{ approved: true; rows: number; total_commission: number }>(
          `/admin/commission-periods/${month}/approve`, { method: 'POST' }),
      markPaid: (month: string) =>
        request<{ paid: true; rows: number; total_commission: number }>(
          `/admin/commission-periods/${month}/mark-paid`, { method: 'POST' }),
    },
    commissionReport: {
      paymentRun: (month: string) =>
        request<CommissionPaymentRun>(`/admin/reports/commissions?month=${encodeURIComponent(month)}`),
      collections: (from: string, to: string) =>
        request<CommissionCollections>(
          `/admin/reports/commissions/collections?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        ),
    },
    commissions: {
      exceptions: () =>
        request<{ exceptions: CommissionException[] }>('/admin/commissions/exceptions'),
      recompute: () =>
        request<CommissionRecomputeResult>('/admin/commissions/recompute', { method: 'POST' }),
      backfillInvoiceDetail: (limit = 200) =>
        request<CommissionBackfillResult>('/admin/commissions/backfill-invoice-detail', {
          method: 'POST',
          body: JSON.stringify({ limit }),
        }),
    },
    companyReport: (month: string) =>
      request<CompanyReport>(`/admin/reports/company?month=${encodeURIComponent(month)}`),
    allTimeReport: () => request<AllTimeReport>('/admin/reports/alltime'),
    hygiene: () => request<HygieneReport>('/admin/hygiene'),
    atRisk: () => request<AtRiskReport>('/admin/at-risk'),
    engagementBudget: () => request<EngagementBudgetReport>('/admin/reports/engagement-budget'),
    engagementInvoices: (engagementId: string) =>
      request<{ invoices: Invoice[] }>(`/admin/engagements/${encodeURIComponent(engagementId)}/invoices`),
    /** Stepped contractual ceiling vs. cumulative burn, by month. */
    engagementBudgetTimeline: (engagementId: string) =>
      request<BudgetTimeline>(`/admin/engagements/${encodeURIComponent(engagementId)}/budget-timeline`),
    invoicing: () => request<InvoiceReport>('/admin/reports/invoicing'),
    /** Worklogs behind one invoice, grouped by the weekly line they were
     *  billed on. Empty `lines` means no detail has been captured or
     *  reconstructed for that invoice yet. */
    invoiceWorklogs: (invoiceId: string) =>
      request<InvoiceWorklogDetail>(`/admin/invoices/${encodeURIComponent(invoiceId)}/worklogs`),
    invoiceDetailCoverage: () =>
      request<InvoiceDetailCoverage>('/admin/invoice-detail/coverage'),
    /** Reconstruct detail for invoices raised before the importer captured
     *  it. Capped per call — repeat until `remaining` reaches 0.
     *  `redo` re-runs invoices last attempted before the latest full sync,
     *  picking up Jira worklog ids that sync has since populated. */
    backfillInvoiceDetail: (limit = 200, verify = false, redo = false) =>
      request<InvoiceDetailBackfillResult>(
        `/admin/invoice-detail/backfill?limit=${limit}&verify=${verify}&redo=${redo}`,
        { method: 'POST' },
      ),
    settings: {
      list: () => request<{ settings: AppSetting[] }>('/admin/settings'),
      set: (key: string, value: string) =>
        request<{ ok: true; key: string; value: string }>(
          `/admin/settings/${encodeURIComponent(key)}`,
          { method: 'PUT', body: JSON.stringify({ value }) },
        ),
    },
    importHarvest: (csvText: string, dryRun = false) =>
      request<HarvestImportResult>(
        `/admin/imports/harvest${dryRun ? '?dry_run=true' : ''}`,
        {
          method: 'POST',
          body: csvText,
          headers: { 'Content-Type': 'text/csv' },
        },
      ),
    deliverableRollup: (params: {
      start: string; end: string;
      issue_type?: 'all' | 'deliverables' | 'overheads';
      group_by?: 'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';
    }) => {
      const qs = new URLSearchParams({
        start: params.start, end: params.end,
        issue_type: params.issue_type ?? 'all',
        group_by: params.group_by ?? 'month',
      }).toString();
      return request<DeliverableRollupReport>(`/admin/reports/deliverable-rollup?${qs}`);
    },
    clawbacks: {
      list: (userId?: string) => {
        const qs = userId ? `?user=${encodeURIComponent(userId)}` : '';
        return request<{ clawbacks: Clawback[] }>(`/admin/clawbacks${qs}`);
      },
      preview: (input: {
        user_jira_id: string;
        original_attribution_month: string;
        work_item_jira_issue_id: string;
        written_off_billable_hours: number;
      }) =>
        request<{ clawback_amount: number }>('/admin/clawbacks/preview', {
          method: 'POST', body: JSON.stringify(input),
        }),
      create: (input: {
        user_jira_id: string;
        original_attribution_month: string;
        work_item_jira_issue_id: string;
        written_off_billable_hours: number;
        notes?: string;
      }) =>
        request<{ clawback: Clawback }>('/admin/clawbacks', {
          method: 'POST', body: JSON.stringify(input),
        }),
      delete: (id: string) =>
        request<{ deleted: true }>(`/admin/clawbacks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      balance: (userId: string) =>
        request<{ pending_balance: number }>(`/admin/clawbacks/${encodeURIComponent(userId)}/balance`),
    },
    audit: (params?: { limit?: number; action?: string; actor?: string }) => {
      const qs = new URLSearchParams();
      qs.set('limit', String(params?.limit ?? 100));
      if (params?.action) qs.set('action', params.action);
      if (params?.actor) qs.set('actor', params.actor);
      return request<{ entries: AuditEntry[] }>(`/admin/audit?${qs.toString()}`);
    },
    impersonate: {
      start: (userId: string) =>
        request<{ impersonating: User }>(`/admin/impersonate/${userId}`, { method: 'POST' }),
      stop: () =>
        request<{ stopped: true }>('/admin/impersonate/stop', {
          method: 'POST',
          skipImpersonation: true,
        }),
    },
    billing: {
      months: () => request<{ months: string[]; defaultInvoiceDates: Record<string, string> }>('/admin/billing/months'),
      preview: (month: string) =>
        request<BillingPreview>('/admin/billing/preview', {
          method: 'POST',
          body: JSON.stringify({ month }),
        }),
      enqueueImport: (params: {
        month: string;
        invoiceDate: string;
        selectedAccounts: string[];
      }) =>
        request<{ runId: string; status: 'queued' }>('/admin/billing/imports', {
          method: 'POST',
          body: JSON.stringify(params),
        }),
      listImports: (limit = 50) =>
        request<{ runs: BillingRunSummary[] }>(`/admin/billing/imports?limit=${limit}`),
      getImport: (id: string) =>
        request<{ run: BillingRunSummary & { selected_accounts: string[] }; accounts: BillingRunAccount[] }>(
          `/admin/billing/imports/${id}`,
        ),
      receivables: (month: string) =>
        request<ReceivablesReport>(`/admin/billing/receivables?month=${encodeURIComponent(month)}`),
      /** Reconcile billing against an uploaded Tempo logged-time .xlsx export.
       *  Posts the raw file bytes; the backend parses + diffs against the month's
       *  billing preview. */
      reconcile: (month: string, file: ArrayBuffer) =>
        request<BillingReconcileResult>(`/admin/billing/reconcile?month=${encodeURIComponent(month)}`, {
          method: 'POST',
          body: file,
          headers: { 'Content-Type': 'application/octet-stream' },
        }),
    },
  },
  // FP&A — gated independently of is_admin (every endpoint applies an fpa_role
  // gate server-side). v1: access management only.
  fpa: {
    me: () => request<FpaMe>('/fpa/me'),
    users: () => request<{ users: FpaUserRow[] }>('/fpa/users'),
    setRole: (userId: string, fpa_role: FpaRole | null) =>
      request<{ updated: true; user_jira_id: string; before: FpaRole | null; after: FpaRole | null }>(
        `/fpa/users/${encodeURIComponent(userId)}/role`,
        { method: 'POST', body: JSON.stringify({ fpa_role }) },
      ),
    grantScope: (userId: string, scopeType: FpaScopeType, scopeId?: string | null) =>
      request<{ granted: true }>(
        `/fpa/users/${encodeURIComponent(userId)}/scope`,
        { method: 'POST', body: JSON.stringify({ scope_type: scopeType, scope_id: scopeId ?? null }) },
      ),
    revokeScope: (userId: string, grantId: string) =>
      request<{ revoked: true }>(
        `/fpa/users/${encodeURIComponent(userId)}/scope/${encodeURIComponent(grantId)}`,
        { method: 'DELETE' },
      ),
    audit: (limit = 100) =>
      request<{ entries: FpaAuditEntry[] }>(`/fpa/audit?limit=${limit}`),
    actuals: (from?: string, to?: string) => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<FpaActualsReport>(`/fpa/actuals${suffix}`);
    },
    departments: (includeInactive = false) =>
      request<{ departments: FpaDepartmentRow[] }>(`/fpa/departments${includeInactive ? '?include_inactive=true' : ''}`),
    departmentRoster: () =>
      request<{ members: Array<{ jira_user_id: string; department_id: string; display_name: string; title: string; role: string }> }>('/fpa/department-roster'),
    planningActuals: (months = 12) =>
      request<PlanningSignals>(`/fpa/planning-actuals?months=${months}`),
    roleEconomics: (months = 12) =>
      request<RoleEconomics>(`/fpa/role-economics?months=${months}`),
    salesCapacity: (months = 12) =>
      request<SalesCapacity>(`/fpa/sales-capacity?months=${months}`),
    marginalModel: (months = 12) =>
      request<MarginalModel>(`/fpa/marginal-model?months=${months}`),
    forecast: (opts: { horizon?: number; lag?: number; duration?: number; uplift?: number } = {}) => {
      const qs = new URLSearchParams();
      if (opts.horizon != null) qs.set('horizon', String(opts.horizon));
      if (opts.lag != null) qs.set('lag', String(opts.lag));
      if (opts.duration != null) qs.set('duration', String(opts.duration));
      if (opts.uplift != null) qs.set('uplift', String(opts.uplift));
      const s = qs.toString();
      return request<TimePhasedForecast>(`/fpa/forecast${s ? `?${s}` : ''}`);
    },
    modelInputs: (months = 12) =>
      request<ModelInputs>(`/fpa/model-inputs?months=${months}`),
    createDepartment: (input: { id: string; name: string; division?: string | null; sort_order?: number }) =>
      request<{ ok: true }>('/fpa/departments', { method: 'POST', body: JSON.stringify(input) }),
    updateDepartment: (id: string, patch: Partial<{ name: string; division: string | null; active: boolean; sort_order: number }>) =>
      request<{ ok: true }>(`/fpa/departments/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    qbo: {
      status: () => request<QboStatus>('/fpa/qbo/status'),
      accounts: () => request<{ accounts: QboAccountRow[] }>('/fpa/qbo/accounts'),
      classes: () => request<{ classes: QboClassRow[] }>('/fpa/qbo/classes'),
      connect: () => request<{ authorize_url: string; state: string }>('/fpa/qbo/connect'),
      callback: (code: string, realmId: string) =>
        request<{ connected: true }>('/fpa/qbo/callback', { method: 'POST', body: JSON.stringify({ code, realmId }) }),
      sync: () => request<{ status: string; accounts: number; classes: number; actuals_upserted: number; from?: string; to?: string }>('/fpa/qbo/sync', { method: 'POST' }),
    },
    setLineMap: (qboAccountId: string, lineCode: string | null) =>
      request<{ ok: true }>('/fpa/maps/line', { method: 'PUT', body: JSON.stringify({ qbo_account_id: qboAccountId, line_code: lineCode }) }),
    setDepartmentMap: (qboClassId: string, departmentId: string | null) =>
      request<{ ok: true }>('/fpa/maps/department', { method: 'PUT', body: JSON.stringify({ qbo_class_id: qboClassId, department_id: departmentId }) }),
    setAccountDepartment: (qboAccountId: string, departmentId: string | null) =>
      request<{ ok: true }>('/fpa/maps/account-department', { method: 'PUT', body: JSON.stringify({ qbo_account_id: qboAccountId, department_id: departmentId }) }),
    rampCurves: () => request<{ ramp_curves: RampCurveRow[] }>('/fpa/ramp-curves'),
    driverRegistry: () => request<{ drivers: DriverDef[] }>('/fpa/driver-registry'),
    // ── Drivers v2 (DB-backed) ──
    createDriver: (body: { key: string; label: string; description?: string; unit: string; scope: string; group?: string; default_value?: number | null }) =>
      request<{ driver: DriverDef }>('/fpa/drivers', { method: 'POST', body: JSON.stringify(body) }),
    updateDriver: (key: string, patch: { label?: string; description?: string; group?: string; default_value?: number | null; is_active?: boolean }) =>
      request<{ driver: DriverDef }>(`/fpa/drivers/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteDriver: (key: string) => request<{ deleted: true }>(`/fpa/drivers/${encodeURIComponent(key)}`, { method: 'DELETE' }),
    // ── Line catalog ──
    catalog: (activeOnly = false) => request<{ catalog: CatalogLineRow[] }>(`/fpa/catalog${activeOnly ? '?active=1' : ''}`),
    // One-shot Planning-v2 cutover: convert cost.fixed.* assumptions → grid items.
    backfillFixedCosts: (body: { apply: boolean; plan_id?: string | null } = { apply: false }) =>
      request<{ apply: boolean; plans: Array<{ plan_id: string; name: string; status: string; items_created: number; assumptions_removed: number; totals_match: boolean | null }> }>(
        '/fpa/backfill/fixed-costs', { method: 'POST', body: JSON.stringify(body) }),
    createCatalogLine: (body: { code: string; label: string; parent_code?: string | null; family: string; kind?: string; sort_order?: number; sensitivity_tier?: number }) =>
      request<{ line: CatalogLineRow }>('/fpa/catalog', { method: 'POST', body: JSON.stringify(body) }),
    updateCatalogLine: (code: string, patch: { label?: string; parent_code?: string | null; sort_order?: number; sensitivity_tier?: number; is_active?: boolean }) =>
      request<{ line: CatalogLineRow }>(`/fpa/catalog/${encodeURIComponent(code)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    plans: {
      list: () => request<{ plans: PlanVersion[] }>('/fpa/plans'),
      get: (id: string) => request<{ plan: PlanVersion; locks: PlanLockRow[] }>(`/fpa/plans/${id}`),
      create: (body: { name: string; kind: PlanKind; horizon_start: string; horizon_end: string; notes?: string | null }) =>
        request<{ plan: PlanVersion }>('/fpa/plans', { method: 'POST', body: JSON.stringify(body) }),
      transition: (id: string, action: 'submit' | 'approve' | 'reject' | 'lock') =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}/${action}`, { method: 'POST' }),
      remove: (id: string) => request<{ ok: true }>(`/fpa/plans/${id}`, { method: 'DELETE' }),
      updateMeta: (id: string, patch: { name?: string; notes?: string | null; horizon_start?: string; horizon_end?: string }) =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      clone: (id: string, body: { name: string; kind?: PlanKind }) =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}/clone`, { method: 'POST', body: JSON.stringify(body) }),
      restate: (id: string, body: { name?: string }) =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}/restate`, { method: 'POST', body: JSON.stringify(body) }),
      supersede: (id: string) => request<{ plan: PlanVersion }>(`/fpa/plans/${id}/supersede`, { method: 'POST' }),
      lockPeriod: (id: string, month: string) =>
        request<{ locked: true }>(`/fpa/plans/${id}/periods/${month}/lock`, { method: 'POST' }),
      unlockPeriod: (id: string, month: string, reason: string) =>
        request<{ unlocked: true }>(`/fpa/plans/${id}/periods/${month}/unlock`, { method: 'POST', body: JSON.stringify({ reason }) }),
      assumptions: (id: string) => request<{ assumptions: PlanAssumptionRow[] }>(`/fpa/plans/${id}/assumptions`),
      setAssumptions: (id: string, entries: Array<{ assumption_key: string; period_month?: string | null; department_id?: string | null; value_numeric?: number | null; value_text?: string | null }>) =>
        request<{ ok: true; upserted: number; cleared: number }>(`/fpa/plans/${id}/assumptions`, { method: 'PATCH', body: JSON.stringify({ entries }) }),
      headcount: (id: string) => request<{ headcount: PlanHeadcountRow[] }>(`/fpa/plans/${id}/headcount`),
      upsertHeadcount: (id: string, record: Partial<PlanHeadcountRow> & { role_label: string; department_id: string; start_month: string }) =>
        request<{ record: PlanHeadcountRow }>(`/fpa/plans/${id}/headcount`, { method: 'POST', body: JSON.stringify(record) }),
      deleteHeadcount: (id: string, hid: string) =>
        request<{ deleted: true }>(`/fpa/plans/${id}/headcount/${hid}`, { method: 'DELETE' }),
      importHeadcountFromUsers: (id: string) =>
        request<{ created: number; skipped: number }>(`/fpa/plans/${id}/headcount/import-users`, { method: 'POST' }),
      importFixedCostsFromQbo: (id: string) =>
        request<{ written: number; months: number; skipped_unmapped: number }>(`/fpa/plans/${id}/fixed-costs/import-qbo`, { method: 'POST' }),
      applyCapacity: (id: string, opts: { minMonths?: number; windowMonths?: number } = {}) =>
        request<{ ok: true; per_person_applied: number; fell_back_to_dept: number; departments_set: number; min_months: number; window_months: number }>(
          `/fpa/plans/${id}/capacity/apply`, { method: 'POST', body: JSON.stringify(opts) }),
      clearCapacity: (id: string) =>
        request<{ ok: true; seats_cleared: number; assumptions_cleared: number }>(
          `/fpa/plans/${id}/capacity/clear`, { method: 'POST' }),
      revenueBuild: (id: string) => request<{ build: TmBuildRow[] }>(`/fpa/plans/${id}/revenue-build`),
      recompute: (id: string) => request<{ ok: true; lineCount: number; months: number }>(`/fpa/plans/${id}/recompute`, { method: 'POST' }),
      lines: (id: string, from?: string, to?: string) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return request<{ lines: PlanLineRow[] }>(`/fpa/plans/${id}/lines${suffix}`);
      },
      lineage: (id: string, line: string, period: string, dept?: string | null) => {
        const qs = new URLSearchParams({ line, period });
        if (dept) qs.set('dept', dept);
        return request<{ line_code: string; department_id: string | null; period_month: string; amount: number; lineage: unknown }>(
          `/fpa/plans/${id}/lineage?${qs.toString()}`,
        );
      },
      contracts: (id: string) => request<{ contracts: PlanRevenueContractRow[] }>(`/fpa/plans/${id}/contracts`),
      upsertContract: (id: string, record: Partial<PlanRevenueContractRow> & { revenue_type: PlanRevenueType; label: string; department_id: string; start_month: string }) =>
        request<{ contract: PlanRevenueContractRow }>(`/fpa/plans/${id}/contracts`, { method: 'POST', body: JSON.stringify(record) }),
      deleteContract: (id: string, cid: string) =>
        request<{ deleted: true }>(`/fpa/plans/${id}/contracts/${cid}`, { method: 'DELETE' }),
      importContracts: (id: string) =>
        request<{ ok: true; created: number; skipped: number }>(`/fpa/plans/${id}/contracts/import`, { method: 'POST' }),
      compEvents: (id: string) => request<{ comp_events: PlanCompEventRow[] }>(`/fpa/plans/${id}/comp-events`),
      upsertCompEvent: (id: string, record: Partial<PlanCompEventRow> & { effective_month: string; event_type: CompEventType }) =>
        request<{ event: PlanCompEventRow }>(`/fpa/plans/${id}/comp-events`, { method: 'POST', body: JSON.stringify(record) }),
      deleteCompEvent: (id: string, cid: string) =>
        request<{ deleted: true }>(`/fpa/plans/${id}/comp-events/${cid}`, { method: 'DELETE' }),
      scenario: (id: string, overrides: {
        assumption_overrides?: Array<{ assumption_key: string; period_month?: string | null; department_id?: string | null; value_numeric: number | null }>;
      }) => request<{ lines: PlanLineRow[] }>(`/fpa/plans/${id}/scenario`, { method: 'POST', body: JSON.stringify(overrides) }),
      scenarios: (id: string) => request<{ scenarios: SavedScenarioRow[] }>(`/fpa/plans/${id}/scenarios`),
      saveScenario: (id: string, name: string, overrides: AssumptionOverride[]) =>
        request<{ scenario: SavedScenarioRow }>(`/fpa/plans/${id}/scenarios`, { method: 'POST', body: JSON.stringify({ name, overrides }) }),
      deleteScenario: (id: string, sid: string) =>
        request<{ ok: true }>(`/fpa/plans/${id}/scenarios/${sid}`, { method: 'DELETE' }),
      formulas: (id: string) => request<{ formulas: PlanFormulaRow[] }>(`/fpa/plans/${id}/formulas`),
      formulaRefs: (id: string) => request<{ groups: FormulaRefGroup[] }>(`/fpa/plans/${id}/formula-refs`),
      modelGraph: (id: string) => request<ModelGraph>(`/fpa/plans/${id}/model-graph`),
      upsertFormula: (id: string, input: { line_code: string; label: string; expression: string; scope?: 'department' | 'company'; sort_order?: number }) =>
        request<{ formula: PlanFormulaRow }>(`/fpa/plans/${id}/formulas`, { method: 'POST', body: JSON.stringify(input) }),
      deleteFormula: (id: string, fid: string) =>
        request<{ ok: true }>(`/fpa/plans/${id}/formulas/${fid}`, { method: 'DELETE' }),
      formulaPreview: (id: string, expression: string, scope: 'department' | 'company') =>
        request<{ months?: Array<{ period_month: string; amount: number }>; error?: string }>(`/fpa/plans/${id}/formula-preview`, { method: 'POST', body: JSON.stringify({ expression, scope }) }),
      // ── Planning v2 — items, blend, reforecast ──
      items: (id: string) => request<{ items: PlanItemRow[] }>(`/fpa/plans/${id}/items`),
      itemsBatch: (id: string, batch: ItemBatchInput) =>
        request<{ ok: true; items_upserted: number; items_deleted: number; values_written: number; overrides_written: number; item_ids: string[] }>(
          `/fpa/plans/${id}/items/batch`, { method: 'PATCH', body: JSON.stringify(batch) }),
      blendedLines: (id: string, from?: string, to?: string) => {
        const qs = new URLSearchParams({ blend: '1' });
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        return request<{ lines: BlendedLineRow[]; actuals_through: string | null }>(`/fpa/plans/${id}/lines?${qs.toString()}`);
      },
      setActualsThrough: (id: string, actuals_through: string | null) =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}/actuals-through`, { method: 'PATCH', body: JSON.stringify({ actuals_through }) }),
      syncPipeline: (id: string, body: { mode?: 'weighted' | 'committed'; close_lag_months?: number; ramp?: 'point' | 'spread_3mo' } = {}) =>
        request<{ ok: true; items: number; deals_considered: number; deals_scheduled: number; unmapped_owners: number; departments: string[]; warnings: string[] }>(
          `/fpa/plans/${id}/pipeline/sync`, { method: 'POST', body: JSON.stringify(body) }),
      seedSubscriptions: (id: string, entries: Array<{ department_id: string; opening_mrr: number; churn_driver?: string; expansion_driver?: string }>) =>
        request<{ ok: true; items: number; warnings: string[] }>(
          `/fpa/plans/${id}/subscriptions/seed`, { method: 'POST', body: JSON.stringify({ entries }) }),
      reforecast: (id: string, body: { name?: string; actuals_through: string }) =>
        request<{ plan: PlanVersion }>(`/fpa/plans/${id}/reforecast`, { method: 'POST', body: JSON.stringify(body) }),
    },
    engagementTypeMap: () => request<{ map: EngagementRevtypeRow[]; available_types: string[] }>('/fpa/maps/engagement-type'),
    setEngagementType: (engagementType: string, revenueType: 'TM' | 'FF' | 'SUB' | null) =>
      request<{ ok: true }>('/fpa/maps/engagement-type', { method: 'PUT', body: JSON.stringify({ engagement_type: engagementType, revenue_type: revenueType }) }),
    variance: (opts: { from?: string; to?: string; baselines?: string[] } = {}) => {
      const qs = new URLSearchParams();
      if (opts.from) qs.set('from', opts.from);
      if (opts.to) qs.set('to', opts.to);
      if (opts.baselines && opts.baselines.length) qs.set('baselines', opts.baselines.join(','));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<VarianceReport>(`/fpa/variance${suffix}`);
    },
    /** Unified statement: pass plan version id(s) as columns; includeActual:false
     *  drops the Actual column. Backs the Plan P&L, Actuals, and Variance views. */
    statement: (opts: { from?: string; to?: string; plan?: string[]; includeActual?: boolean } = {}) => {
      const qs = new URLSearchParams();
      if (opts.from) qs.set('from', opts.from);
      if (opts.to) qs.set('to', opts.to);
      // Send `plan` whenever the caller is explicit (incl. [] = actuals-only, no
      // baseline columns); omit entirely to let the server pick default baselines.
      if (opts.plan !== undefined) qs.set('plan', opts.plan.join(','));
      if (opts.includeActual === false) qs.set('actual', 'false');
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<FpaStatement>(`/fpa/statement${suffix}`);
    },
    varianceContributions: (category: string, period: string, baseline?: string | null) => {
      const qs = new URLSearchParams({ category, period });
      if (baseline) qs.set('baseline', baseline);
      return request<ContributionsResult>(`/fpa/variance/contributions?${qs.toString()}`);
    },
    varianceNotes: (filter: { plan?: string | null; category?: string; period?: string } = {}) => {
      const qs = new URLSearchParams();
      if (filter.plan) qs.set('plan', filter.plan);
      if (filter.category) qs.set('category', filter.category);
      if (filter.period) qs.set('period', filter.period);
      return request<{ notes: FpaVarianceNote[] }>(`/fpa/variance/notes?${qs.toString()}`);
    },
    addVarianceNote: (body: { plan_version_id?: string | null; category: string; department_id?: string | null; period_month?: string | null; period_grain?: string; body: string }) =>
      request<{ note: FpaVarianceNote }>('/fpa/variance/notes', { method: 'POST', body: JSON.stringify(body) }),
    exportVariance: (opts: { from: string; to: string; baselines?: string[] }) => {
      const qs = new URLSearchParams({ from: opts.from, to: opts.to });
      if (opts.baselines && opts.baselines.length) qs.set('baselines', opts.baselines.join(','));
      return downloadFile(`/fpa/export/variance?${qs.toString()}`, `variance-${opts.from}_${opts.to}.csv`);
    },
    exportPlan: (id: string, name: string) => downloadFile(`/fpa/export/plan/${id}`, `plan-${name.replace(/[^\w.-]+/g, '_')}.csv`),
    // ── Close & monitoring (Phase 6) ──
    close: (month: string) => request<{ calendar: CloseCalendarStep[]; checklist: CloseChecklistItem[] }>(`/fpa/close/${month}`),
    completeCloseStep: (month: string, step: string, complete: boolean) =>
      request<{ ok: true }>(`/fpa/close/${month}/steps/${step}/complete`, { method: 'POST', body: JSON.stringify({ complete }) }),
    addChecklistItem: (month: string, body: { task_name: string; due_date?: string | null }) =>
      request<{ item: CloseChecklistItem }>(`/fpa/close/${month}/checklist`, { method: 'POST', body: JSON.stringify(body) }),
    updateChecklistItem: (month: string, tid: string, patch: { task_name?: string; due_date?: string | null; completed?: boolean }) =>
      request<{ item: CloseChecklistItem }>(`/fpa/close/${month}/checklist/${tid}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    glRecon: (month: string) => request<{ recon: GlReconRow }>(`/fpa/gl-recon/${month}`),
    addReconItem: (month: string, body: { amount: number; description: string }) =>
      request<{ recon: GlReconRow }>(`/fpa/gl-recon/${month}/items`, { method: 'POST', body: JSON.stringify(body) }),
    signOffRecon: (month: string) => request<{ recon: GlReconRow }>(`/fpa/gl-recon/${month}/sign-off`, { method: 'POST' }),
    planQuality: (id: string) => request<PlanQualitySummary>(`/fpa/plans/${id}/quality`),
    recomputeQuality: (id: string, throughMonth?: string) =>
      request<{ ok: true; rows: number; through: string | null }>(`/fpa/plans/${id}/quality/recompute`, { method: 'POST', body: JSON.stringify({ through_month: throughMonth ?? null }) }),
  },

  sales: {
    /** Team + individual sales performance. `rep` (manager-only) drills into a
     *  specific HubSpot owner; ignored for non-managers (server scopes to self). */
    performance: (months = 12, rep?: string) => {
      const qs = new URLSearchParams();
      qs.set('months', String(months));
      if (rep) qs.set('rep', rep);
      return request<SalesPerformance>(`/sales/performance?${qs.toString()}`);
    },
    /** The caller's own commission history. Managers may pass `rep` to drill
     *  into another owner; for everyone else the server forces self. */
    commissions: (months = 12, rep?: string) => {
      const qs = new URLSearchParams();
      qs.set('months', String(months));
      if (rep) qs.set('rep', rep);
      return request<RepCommissionSummary>(`/sales/commissions?${qs.toString()}`);
    },
    quotas: (year: number) => request<SalesQuotaGrid>(`/sales/quotas?year=${year}`),
    saveQuotas: (rows: SalesQuotaRow[]) =>
      request<{ saved: number }>('/sales/quotas', { method: 'PUT', body: JSON.stringify({ rows }) }),
  },

  dealOnboarding: {
    /** Closed-won deals + their computed onboarding checklist. Defaults to deals
     *  closed on/after go-live; pass includeHistorical for the pre-launch backlog,
     *  includeArchived for records auto-archived after completion/dismissal. */
    list: (opts?: { status?: string; includeHistorical?: boolean; includeArchived?: boolean }) => {
      const qs = new URLSearchParams();
      if (opts?.status) qs.set('status', opts.status);
      if (opts?.includeHistorical) qs.set('includeHistorical', 'true');
      if (opts?.includeArchived) qs.set('includeArchived', 'true');
      const q = qs.toString();
      return request<{ records: OnboardingRecord[]; go_live_date: string }>(`/deal-onboarding${q ? `?${q}` : ''}`);
    },
    /** Selectable ChargeOver invoices for the picker. */
    invoices: (q?: string) =>
      request<{ invoices: SelectableInvoice[] }>(`/deal-onboarding/invoices${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    /** Cycle-time stats per transition. */
    stats: () => request<{ stats: OnboardingCycleStat[] }>(`/deal-onboarding/stats`),
    /** Distinct engagement types (for the required-steps matrix). */
    engagementTypes: () => request<{ types: string[] }>(`/deal-onboarding/engagement-types`),
    /** Historical deal→account-key backfill from a spreadsheet. Posted as raw
     *  .xlsx bytes; the server parses it, because Cloud SQL isn't reachable
     *  from a workstation so the equivalent CLI can't run against real data.
     *  Dry run classifies and reports without writing. */
    importLinks: async (file: File, dryRun: boolean, sheet?: string) => {
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dry_run', 'true');
      if (sheet) qs.set('sheet', sheet);
      const q = qs.toString();
      return request<DealLinkImportResult>(`/deal-onboarding/import-links${q ? `?${q}` : ''}`, {
        method: 'POST',
        body: await file.arrayBuffer(),
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });
    },
    /** Force-send the Slack at-risk digest now (testing / catch-ups). */
    sendDigest: () =>
      request<{ result: { skipped: boolean; reason?: string; overdue: number; due_soon: number; posted: boolean } }>(
        `/deal-onboarding/digest`, { method: 'POST' }),
    /** Attach an invoice → derive account key + auto-tie engagement. */
    selectInvoice: (id: string, invoiceId: string) =>
      request<{ record: OnboardingRecord }>(`/deal-onboarding/${id}/invoice`, { method: 'POST', body: JSON.stringify({ invoiceId }) }),
    /** Detach the invoice (and the account/engagement derived from it). */
    delinkInvoice: (id: string) =>
      request<{ record: OnboardingRecord }>(`/deal-onboarding/${id}/invoice`, { method: 'DELETE' }),
    /** Update manual step dates / notes / status. */
    update: (id: string, patch: OnboardingPatch) =>
      request<{ record: OnboardingRecord }>(`/deal-onboarding/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  },

  /** Per-engagement workspace. `report_engagement_workspace` opens the shell;
   *  the money and contract sections are gated separately server-side, so the
   *  payload reflects what this caller may actually see. */
  engagementWorkspace: {
    /** The picker. No money — gated on the shell key alone. */
    list: () => request<{ engagements: EngagementIndexRow[] }>('/admin/engagements'),
    get: (engagementId: string) =>
      request<EngagementWorkspace>(`/admin/engagements/${encodeURIComponent(engagementId)}/workspace`),
    time: (engagementId: string) =>
      request<WorkspaceTime>(`/admin/engagements/${encodeURIComponent(engagementId)}/workspace/time`),
    /** 403s without report_invoicing or billing_receivables — "not allowed"
     *  must not look like "nothing here". */
    money: (engagementId: string) =>
      request<WorkspaceMoney | null>(`/admin/engagements/${encodeURIComponent(engagementId)}/workspace/money`),
    delivery: (engagementId: string) =>
      request<{ deliverables: WorkspaceDeliverable[]; wbs: WbsDeliverable[] }>(
        `/admin/engagements/${encodeURIComponent(engagementId)}/workspace/delivery`),
    /** 403s without report_profitability — cost data is granted deliberately. */
    profitability: (engagementId: string) =>
      request<WorkspaceProfitability>(`/admin/engagements/${encodeURIComponent(engagementId)}/workspace/profitability`),
  },

  /** Blended cost rate schedule — the denominator of every margin figure. */
  costRates: {
    list: () => request<{ rates: CostRate[] }>('/admin/cost-rates'),
    save: (input: { effective_from: string; hourly_cost_rate: number; notes?: string | null }) =>
      request<{ rate: CostRate }>('/admin/cost-rates', { method: 'PUT', body: JSON.stringify(input) }),
    remove: (effectiveFrom: string) =>
      request<{ ok: true }>(`/admin/cost-rates/${encodeURIComponent(effectiveFrom)}`, { method: 'DELETE' }),
  },

  /** Engagement contract terms + change orders — what the client signed, which
   *  is what billing gets checked against. Reads need report_engagement_budget;
   *  every write needs admin_engagement_contracts. */
  engagementContracts: {
    /** By engagement or by deal. Resolves to `{ contract: null }` when nothing
     *  has been captured — that's the normal starting state, not an error. */
    find: (opts: { engagementId?: string; dealId?: string }) => {
      const qs = new URLSearchParams();
      if (opts.engagementId) qs.set('engagementId', opts.engagementId);
      if (opts.dealId) qs.set('dealId', opts.dealId);
      return request<{ contract: EngagementContract | null }>(`/admin/engagement-contracts?${qs.toString()}`);
    },
    /** Create or correct the BASELINE terms. A negotiated change belongs in a
     *  change order, which preserves what the numbers were beforehand. */
    save: (input: ContractInput) =>
      request<{ contract: EngagementContract }>('/admin/engagement-contracts', {
        method: 'PUT', body: JSON.stringify(input),
      }),
    remove: (id: string) =>
      request<{ ok: true }>(`/admin/engagement-contracts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    // All three change-order calls return the whole recomputed contract, so the
    // UI never renders a fresh change order beside a stale total.
    addChangeOrder: (contractId: string, input: ChangeOrderInput) =>
      request<{ contract: EngagementContract }>(
        `/admin/engagement-contracts/${encodeURIComponent(contractId)}/change-orders`,
        { method: 'POST', body: JSON.stringify(input) }),
    updateChangeOrder: (changeOrderId: string, input: ChangeOrderInput) =>
      request<{ contract: EngagementContract }>(
        `/admin/engagement-contracts/change-orders/${encodeURIComponent(changeOrderId)}`,
        { method: 'PATCH', body: JSON.stringify(input) }),
    deleteChangeOrder: (changeOrderId: string) =>
      request<{ contract: EngagementContract }>(
        `/admin/engagement-contracts/change-orders/${encodeURIComponent(changeOrderId)}`,
        { method: 'DELETE' }),
  },
};

// ─── Engagement contracts ───────────────────────────────────────────────────

export type ContractType = 'fixed_fee' | 'estimate' | 'estimate_sne';
export type ContractSource = 'manual' | 'hubspot';
/** The five figures a contract and its change orders move. */
export type ContractFigure =
  'fixed_fee' | 'estimate_low' | 'estimate_median' | 'estimate_high' | 'not_to_exceed';

/** One figure's baseline, what change orders did to it, and where it stands
 *  now vs. once every signed change order has taken effect. */
export interface FigureState {
  baseline: number | null;
  applied_delta: number;
  pending_delta: number;
  /** baseline + applied_delta — the number to hold billing against TODAY. */
  current: number | null;
  future: number | null;
}

export interface ContractChangeOrder {
  id: string;
  contract_id: string;
  reference: string | null;
  scope_change: string;
  effective_date: string;
  /** Absent keys mean this change order leaves that figure alone. */
  deltas: Partial<Record<ContractFigure, number>>;
  source: ContractSource;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  /** False while the effective date is still in the future — signed, but its
   *  money hasn't landed on the ceiling yet. */
  is_effective: boolean;
}

export interface EngagementContract {
  id: string;
  jira_engagement_issue_id: string | null;
  hubspot_deal_id: string | null;
  contract_type: ContractType;
  currency: string;
  source: ContractSource;
  contract_reference: string | null;
  signed_date: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  /** Only the figures this contract type carries are present. */
  figures: Partial<Record<ContractFigure, FigureState>>;
  change_orders: ContractChangeOrder[];
}

export interface ContractInput {
  jira_engagement_issue_id?: string | null;
  hubspot_deal_id?: string | null;
  contract_type: ContractType;
  fixed_fee_amount?: number | null;
  estimate_low?: number | null;
  estimate_median?: number | null;
  estimate_high?: number | null;
  not_to_exceed_amount?: number | null;
  currency?: string;
  contract_reference?: string | null;
  signed_date?: string | null;
  notes?: string | null;
}

export interface ChangeOrderInput {
  reference?: string | null;
  scope_change: string;
  effective_date: string;
  delta_fixed_fee?: number | null;
  delta_estimate_low?: number | null;
  delta_estimate_median?: number | null;
  delta_estimate_high?: number | null;
  delta_not_to_exceed?: number | null;
  notes?: string | null;
}

/** Which contractual lines burn has passed. Independent rather than nested:
 *  passing the low estimate starts a conversation, passing the shall-not-exceed
 *  is a breach of terms. */
export interface ThresholdFlags {
  over_low: boolean;
  over_median: boolean;
  over_high: boolean;
  over_not_to_exceed: boolean;
  over_fixed_fee: boolean;
  worst: 'none' | 'low' | 'median' | 'high' | 'not_to_exceed' | 'fixed_fee';
}

export interface BudgetTimelinePoint {
  month: string;
  burned_in_month: number;
  burned_cumulative: number;
  fixed_fee: number | null;
  estimate_low: number | null;
  estimate_median: number | null;
  estimate_high: number | null;
  not_to_exceed: number | null;
  is_future: boolean;
}

export interface BudgetTimeline {
  engagement_id: string;
  contract_type: ContractType | null;
  currency: string;
  change_orders: Array<{
    id: string;
    reference: string | null;
    scope_change: string;
    effective_date: string;
    month: string;
    is_effective: boolean;
    deltas: Partial<Record<ContractFigure, number>>;
  }>;
  points: BudgetTimelinePoint[];
}

export interface OnboardingChecklistStep {
  key: string;
  label: string;
  done: boolean;
  derived: boolean;
  detail: string | null;
}

export type OnboardingStageStatus = 'complete' | 'on_track' | 'due_soon' | 'overdue' | 'pending';
export type OnboardingOverallStatus = 'complete' | 'on_track' | 'due_soon' | 'overdue' | 'not_started';

export interface OnboardingSlaStage {
  key: string;
  label: string;
  started_at: string | null;
  completed_at: string | null;
  due_date: string | null;
  target_days: number | null;
  duration_days: number | null;
  elapsed_days: number | null;
  status: OnboardingStageStatus;
}

export interface OnboardingSla {
  overall: OnboardingOverallStatus;
  current_stage: string | null;
  current_waiting_days: number | null;
  stages: OnboardingSlaStage[];
}

export interface OnboardingCycleStat {
  key: string;
  label: string;
  count: number;
  median: number | null;
  p90: number | null;
  avg: number | null;
}

// ─── Engagement workspace ───────────────────────────────────────────────────

/** Every worklog lands in exactly one state, in this precedence: a flagged
 *  worklog can't be judged billable yet, so review outranks everything. */
export type WorklogState = 'needs_review' | 'non_billable' | 'billed' | 'unbilled';

export interface TimeStateTotals { hours: number; value: number; worklog_count: number }

interface TimeBucket extends TimeStateTotals {
  billed_hours: number;
  unbilled_hours: number;
  non_billable_hours: number;
  needs_review_hours: number;
}

export interface WorkspaceTimeRow {
  tempo_worklog_id: string;
  work_date: string;
  author_jira_user_id: string | null;
  author_name: string | null;
  deliverable_jira_issue_id: string;
  deliverable_key: string | null;
  deliverable_summary: string | null;
  logged_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  description: string | null;
  state: WorklogState;
  billable_rate: number | null;
  value: number;
  chargeover_invoice_id: string | null;
  invoice_number: string | null;
}

export interface WorkspaceTime {
  logged_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  by_state: Record<WorklogState, TimeStateTotals>;
  by_person: Array<TimeBucket & { jira_user_id: string | null; name: string | null }>;
  /** Segmented by the role of whoever logged the time — the mix question. */
  by_role: Array<TimeBucket & { role: string }>;
  by_deliverable: Array<TimeBucket & { jira_issue_id: string; jira_issue_key: string | null; summary: string | null }>;
  by_week: Array<TimeBucket & { week_start: string }>;
  rows: WorkspaceTimeRow[];
  /** Totals stay complete when true — only the detail list is capped. */
  rows_truncated: boolean;
}

export interface WorkspaceEngagement {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string | null;
  status: string | null;
  engagement_type: string | null;
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  budget_amount: number | null;
  created_date: string | null;
  welcome_email_sent: string | null;
  pre_kickoff_date: string | null;
  kickoff_date: string | null;
  delivery_manager_name: string | null;
  solution_architect_name: string | null;
  deliverable_count: number;
  completed_count: number;
  first_work_date: string | null;
  latest_work_date: string | null;
  burned_value: number;
}

export interface WorkspaceDeal {
  hubspot_deal_id: string;
  deal_name: string | null;
  amount: number | null;
  close_date: string | null;
  deal_type: string | null;
  deal_source: string | null;
  owner_name: string | null;
  onboarding_status: string | null;
  chargeover_invoice_id: string | null;
}

export interface CostRate {
  id: number;
  effective_from: string;
  hourly_cost_rate: number;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface ProfitabilityBucket {
  logged_hours: number;
  billable_hours: number;
  revenue: number;
  cost: number;
  margin: number;
  /** Null when revenue is zero — undefined, not 0%. */
  margin_pct: number | null;
}

export interface WorkspaceProfitability extends ProfitabilityBucket {
  /** Hours no cost rate covers. Cost is understated while this is non-zero. */
  uncosted_hours: number;
  effective_cost_rate: number | null;
  by_person: Array<ProfitabilityBucket & { jira_user_id: string | null; name: string | null }>;
  by_role: Array<ProfitabilityBucket & { role: string }>;
  by_deliverable: Array<ProfitabilityBucket & { jira_issue_id: string; jira_issue_key: string | null; summary: string | null }>;
  by_month: Array<ProfitabilityBucket & { month: string }>;
}

export interface WorkspaceTransaction {
  chargeover_transaction_id: string;
  transaction_type: string | null;
  transaction_date: string | null;
  amount: number;
  applied_amount: number;
  remaining_amount: number;
  is_deposit: boolean;
  raw_status: string | null;
}

export interface WorkspaceMoney {
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  /** Invoices are raised per ChargeOver customer, not per engagement — when
   *  true, the figures cover more than this engagement and the UI says so. */
  account_has_other_engagements: boolean;
  totals: {
    billed: number; paid: number; credit: number; writeoff: number;
    deposit_applied: number; settled_off_invoice: number;
    outstanding: number; overdue: number; draft: number;
    deposit_remaining: number; credit_remaining: number;
    /** Passes through to the payment processor — not ours to deliver with. */
    fees: number;
    /** Billed with no line-item split, so any fee is still hidden inside it. */
    unsplit_billed: number;
  };
  ageing: { d1_30: number; d31_60: number; d61_90: number; d91_plus: number };
  invoices: InvoiceReportInvoice[];
  transactions: WorkspaceTransaction[];
}

export interface WbsNode {
  jira_issue_id: string;
  jira_issue_key: string | null;
  summary: string | null;
  issue_type: string | null;
  logged_hours: number;
  billable_hours: number;
  value: number;
  worklog_count: number;
  /** Jira original estimate in hours. NULL = not estimated, which is different
   *  from estimated at zero — render the two differently. */
  estimate_hours: number | null;
  /** logged − estimate; positive is over. Null when unestimated. */
  variance_hours: number | null;
}

export interface WbsDeliverable extends WbsNode {
  status: string | null;
  attribution_month: string | null;
  children: WbsNode[];
  /** Time logged straight onto the deliverable rather than any child issue. */
  direct_hours: number;
  /** Σ of the children's estimates, shown alongside the deliverable's own
   *  rather than replacing it — the two are entered independently in Jira. */
  children_estimate_hours: number | null;
}

export interface WorkspaceDeliverable {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string | null;
  status: string | null;
  due_date: string | null;
  attribution_month: string | null;
  billable_rate: number | null;
  delivery_manager_name: string | null;
  solution_architect_name: string | null;
  logged_hours: number;
  billable_hours: number;
  value: number;
  child_issue_count: number;
  last_work_date: string | null;
}

/** A row on the workspace picker. Carries no financial figures by design. */
export interface EngagementIndexRow {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string | null;
  status: string | null;
  engagement_type: string | null;
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  delivery_manager_name: string | null;
  deliverable_count: number;
  latest_work_date: string | null;
}

export interface EngagementWorkspace {
  engagement: WorkspaceEngagement;
  deal: WorkspaceDeal | null;
  /** Null when the caller lacks report_engagement_budget — omitted server-side,
   *  never sent and blanked. Check `visibility.contract` to tell "not allowed"
   *  from "none captured". */
  contract: EngagementContract | null;
  ceiling: number | null;
  ceiling_source: 'contract' | 'jira' | null;
  remaining: number | null;
  pct_consumed: number | null;
  thresholds: ThresholdFlags | null;
  visibility: { contract: boolean; money: boolean; profitability: boolean };
}

/** Outcome of the historical deal→account-key backfill. Every "would" figure
 *  is computed the same way in dry run and write, so the preview is the plan. */
export interface DealLinkImportResult {
  parsed: number;
  skipped_incomplete: number;
  duplicate_deal_ids: string[];
  inserted: number;
  account_key_set: number;
  already_matching: number;
  /** Sheet disagrees with a key already set. Skipped — never overwritten. */
  conflicts: Array<{
    hubspot_deal_id: string;
    deal_name: string | null;
    file_account_key: string;
    db_account_key: string;
  }>;
  /** Not in hubspot_deals → no close_date → lands in the LIVE queue, not
   *  filed as historical. Worth fixing in the sheet before writing. */
  unknown_deals: string[];
  /** Matches no tempo_account — imports, but will never tie an engagement. */
  unknown_account_keys: string[];
  /** Has an account but no engagement on it — imports, ties nothing. */
  account_keys_without_engagement: string[];
  /** Populated on a write run only. */
  tied: number | null;
  contracts_stamped: number | null;
  dry_run: boolean;
  sheet: string;
  available_sheets: string[];
}

export interface OnboardingRecord {
  id: string;
  hubspot_deal_id: string;
  deal_name: string | null;
  closed_at: string | null;
  account_key: string | null;
  chargeover_invoice_id: string | null;
  jira_engagement_issue_id: string | null;
  jira_engagement_key: string | null;
  status: string;
  completed_at: string | null;
  handoff_doc_received_at: string | null;
  handoff_doc_url: string | null;
  welcome_email_sent: string | null;
  pre_kickoff_date: string | null;
  kickoff_date: string | null;
  sa_assigned_at: string | null;
  dm_assigned_at: string | null;
  notes: string | null;
  deal_amount: number | null;
  account_name: string | null;
  invoice_number: string | null;
  invoice_status: string | null;
  invoice_total: number | null;
  invoice_paid: number | null;
  invoice_balance: number | null;
  invoice_pdf_url: string | null;
  deal_type: string | null;
  deal_source: string | null;
  deal_weighted_amount: number | null;
  deal_created_at: string | null;
  deal_owner: string | null;
  /** Who shares credit for the deal, from HubSpot — this is what apportions
   *  the deal's sales commission. Empty = no split, 100% to the deal owner.
   *  split_percentage is a FRACTION (0..1), as HubSpot stores it. */
  deal_splits: DealSplitShare[];
  pipeline_label: string | null;
  stage_label: string | null;
  engagement_summary: string | null;
  engagement_status: string | null;
  engagement_type: string | null;
  engagement_start_target: string | null;
  engagement_start_actual: string | null;
  engagement_planned_ship_date: string | null;
  engagement_created_at: string | null;
  engagement_budget: number | null;
  engagement_sa_user_id: string | null;
  engagement_dm_user_id: string | null;
  engagement_sa_name: string | null;
  engagement_dm_name: string | null;
  invoice_issued_at: string | null;
  invoice_due_date: string | null;
  invoice_paid_at: string | null;
  checklist: OnboardingChecklistStep[];
  checklist_done: number;
  checklist_total: number;
  sla: OnboardingSla;
}

export interface SelectableInvoice {
  chargeover_invoice_id: string;
  invoice_number: string | null;
  account_key: string | null;
  account_name: string | null;
  customer_name: string | null;
  raw_status: string | null;
  total_amount: number | null;
  invoice_date: string | null;
}

export interface OnboardingPatch {
  notes?: string | null;
  status?: string;
}

export interface CompanyReportRow {
  user: {
    jira_user_id: string;
    display_name: string;
    email: string;
    role: Role;
    bonus_role: BonusRole | null;
    title: string;
    team: string;
  };
  effective_bonus_role: BonusRole;
  shipped_value: number;
  bonus_tier: number | null;
  bonus_multiplier: number | null;
  gross_bonus: number | null;
  is_locked: boolean;
  total_wip: number;
  potential_total: number;
  potential_tier: number | null;
  potential_multiplier: number | null;
  potential_gross_bonus: number | null;
  potential_bonus_delta: number | null;
}

export interface CompanyReport {
  month: string;
  row_count: number;
  totals: {
    shipped_value: number;
    gross_bonus: number;
    total_wip: number;
    potential_gross_bonus: number;
  };
  rows: CompanyReportRow[];
}

export interface AllTimeIndividualRow {
  user: {
    jira_user_id: string;
    display_name: string;
    role: Role;
    bonus_role: BonusRole | null;
    title: string;
    team: string;
  };
  effective_bonus_role: BonusRole;
  shipped_value: number;
  wip_value: number;
  canceled_value: number;
  total_value: number;
}

export interface AllTimeTeamRow {
  director: {
    jira_user_id: string;
    display_name: string;
    role: Role;
    title: string;
    team: string;
  };
  member_count: number;
  shipped_value: number;
  wip_value: number;
  canceled_value: number;
  total_value: number;
  includes_personal: boolean;
}

export interface AllTimeReport {
  individuals: AllTimeIndividualRow[];
  teams: AllTimeTeamRow[];
  totals: { shipped_value: number; wip_value: number; canceled_value: number };
}

export interface HygieneDeliverableRow {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string;
  status: string;
  due_date: string | null;
  completed_date: string | null;
  tempo_account_id: string | null;
  billable_rate: number | string | null;
  delivery_manager_jira_user_id: string | null;
  solution_architect_jira_user_id: string | null;
}

export interface HygieneUserRow {
  jira_user_id: string;
  display_name: string;
  email: string;
  role: Role;
  title?: string;
  team?: string;
}

export interface HygieneWorklogRow {
  tempo_worklog_id: string;
  description: string | null;
  logged_hours: string;
  work_date: string;
  deliverable_jira_issue_id: string;
  deliverable_key: string | null;
  deliverable_summary: string | null;
  author_jira_user_id: string | null;
  author_display_name: string | null;
  score: number;
  bucket: 'ok' | 'review' | 'poor';
  reasons: string[];
}

export interface HygieneReport {
  deliverables: {
    missing_delivery_manager: HygieneDeliverableRow[];
    missing_solution_architect: HygieneDeliverableRow[];
    missing_tempo_account: HygieneDeliverableRow[];
    zero_billable_rate: HygieneDeliverableRow[];
    completed_without_date: HygieneDeliverableRow[];
  };
  users: {
    directors_without_bonus_role: HygieneUserRow[];
    missing_manager: HygieneUserRow[];
    missing_team: HygieneUserRow[];
  };
  worklogs: {
    poor_descriptions:   HygieneWorklogRow[];
    review_descriptions: HygieneWorklogRow[];
  };
}

export interface AtRiskDeliverableRow {
  jira_issue_id: string;
  jira_issue_key: string;
  issue_type: IssueType;
  summary: string;
  status: string;
  /** Jira Planned Ship Date — drives the Overdue bucket (when we plan to
   *  actually ship), in place of the client-deadline due_date. */
  planned_ship_date: string | null;
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  delivery_manager_display_name: string | null;
  solution_architect_display_name: string | null;
  days_overdue?: number;
  last_work_date?: string | null;
  days_since_activity?: number;
}

export interface AtRiskReport {
  overdue: AtRiskDeliverableRow[];
  stale: AtRiskDeliverableRow[];
  unassigned: AtRiskDeliverableRow[];
}

export type InvoiceLifecycle = 'draft' | 'outstanding' | 'due' | 'overdue' | 'paid' | 'void';

export interface InvoiceSummary {
  tempo_account_id: string;
  /** ChargeOver raw status → count of invoices. UI displays these verbatim. */
  status_counts: Record<string, number>;
  status_balance: Record<string, number>;
  billed_total: number;
  paid_total: number;
  overdue_balance: number;
  outstanding_balance: number;
  writeoff_total: number;
  credit_total: number;
  /** Deposit credit drawn down against these invoices. Shown rather than folded
   *  into paid_total: a balance that closed because a prepayment covered it is
   *  a different fact from cash arriving. */
  deposit_applied_total: number;
  /** CC processing / convenience fees billed. NOT available to deliver with —
   *  it passes through to the payment processor — so it sits beside billed
   *  rather than inside it. */
  fees_total: number;
  /** Billed on invoices with no line-item split, where any fee is still inside
   *  billed_total and missing from fees_total. Distinguishes "no fees" from
   *  "not split yet". */
  unsplit_billed_total: number;
  invoice_count: number;
  overdue_1_30_count: number;
  overdue_31_60_count: number;
  overdue_61_90_count: number;
  overdue_91_plus_count: number;
  overdue_1_30_balance: number;
  overdue_31_60_balance: number;
  overdue_61_90_balance: number;
  overdue_91_plus_balance: number;
  next_due_date: string | null;
  most_overdue_days: number | null;
}

export interface Invoice {
  chargeover_invoice_id: string;
  tempo_account_id: string | null;
  chargeover_customer_id: string | null;
  invoice_number: string | null;
  raw_status: string | null;
  lifecycle: InvoiceLifecycle;
  invoice_date: string | null;
  date_issued: string | null;
  due_date: string | null;
  total_amount: number;
  paid_amount: number;
  balance_amount: number;
  writeoff_amount: number;
  credit_amount: number;
  currency: string | null;
  refreshed_at: string;
}

export interface EngagementBudgetRow {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string;
  status: string;
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  /** Sourced from ChargeOver at refresh time. Drives is_fixed_fee. */
  billing_model: 'hourly' | 'fixed_fee' | 'unknown';
  deliverable_count: number;
  completed_count: number;
  /** Sum of child deliverables' fixed_fee_amount. Null/0 when no dollar
   *  amount has been entered — is_fixed_fee can still be true (via account
   *  billing_model) with a null contract_value. */
  contract_value: number | null;
  burned_value: number;
  remaining: number | null;
  pct_consumed: number | null;
  is_fixed_fee: boolean;
  is_overrun: boolean;
  latest_work_date: string | null;
  /** Null when no invoices exist for this engagement's Tempo account. */
  billing: InvoiceSummary | null;
  // ── Captured contract terms. All null/0 until someone fills them in; those
  // engagements still run on the Jira budget field alone. ──
  contract_id: string | null;
  contract_type: ContractType | null;
  contract_source: ContractSource | null;
  contract_reference: string | null;
  /** Per-figure baseline/current/future. `current` is the number to hold
   *  billing against today. */
  contract_figures: Partial<Record<ContractFigure, FigureState>> | null;
  change_order_count: number;
  /** Signed change orders whose effective date hasn't arrived yet. */
  pending_change_order_count: number;
  /** Null without a captured contract — the Jira budget alone has no band. */
  thresholds: ThresholdFlags | null;
}

/** One worklog as it stood when the invoice line was captured. Every field is
 *  a point-in-time copy — `worklogs` gets re-snapshotted and pruned, so this
 *  is deliberately NOT a live view of the worklog. */
export interface InvoiceWorklog {
  tempo_worklog_id: string;
  /** Jira's numeric issue id. Always present; /browse/{id} redirects. */
  jira_issue_id: string | null;
  /** Readable issue key. NULL for worklogs snapshotted before the sync
   *  started carrying it AND whose issue is in neither the deliverable nor
   *  child-issue tables. */
  jira_issue_key: string | null;
  /** Jira's own worklog id — the only one that anchors the worklog tab.
   *  NULL until a full sync has re-snapshotted the worklog. */
  jira_worklog_id: string | null;
  author_jira_user_id: string | null;
  author_display_name: string | null;
  work_date: string | null;
  billable_hours: number;
  description: string | null;
}

/** One row of the ORP ↔ ChargeOver comparison for an invoice line.
 *  - `match`           present on both sides
 *  - `only_orp`        ORP has it, ChargeOver's COMPLETE list doesn't
 *  - `only_chargeover` ChargeOver billed it, ORP lacks it — always conclusive
 *  - `unprovable`      ORP has it, ChargeOver's list is clipped so its absence
 *                      there proves nothing. Not a disagreement. */
/** Enough of a worklog to identify it without leaving ORP — a bare ID just
 *  relocates the lookup problem into Tempo. */
export interface DiffWorklogFacts {
  tempo_worklog_id: string;
  work_date: string | null;
  author_display_name: string | null;
  jira_issue_key: string | null;
  jira_issue_id: string | null;
  jira_worklog_id: string | null;
  billable_hours: number;
  description: string | null;
  /** Where ORP places this worklog today. Set for rows ChargeOver billed that
   *  the rebuild missed — "it moved" is usually the whole explanation. */
  current_account_key: string | null;
  current_account_name: string | null;
  /** False when ORP has no worklog with this ID at all — deleted from Tempo. */
  in_orp: boolean;
}

export interface WorklogIdDiffRow {
  orp: string | null;
  chargeover: string | null;
  status: 'match' | 'only_orp' | 'only_chargeover' | 'unprovable';
  worklog: DiffWorklogFacts | null;
}

export interface WorklogIdDiff {
  rows: WorklogIdDiffRow[];
  matched: number;
  only_orp: number;
  only_chargeover: number;
  unprovable: number;
  /** ChargeOver's custom_2 hit its 245-char cap and was clipped. */
  truncated: boolean;
  /** Something genuinely disagrees. `unprovable` never counts toward this. */
  has_conflict: boolean;
}

export interface InvoiceWorklogLine {
  service_from_date: string;
  service_to_date: string;
  descrip: string | null;
  line_quantity: number | null;
  /** NULL on reconstructed lines — the rate applied at the time is not
   *  recoverable from worklog data. */
  line_rate: number | null;
  line_total: number | null;
  /** 'import' = captured as the line was written to ChargeOver
   *  (authoritative); 'reconstructed' = rebuilt afterwards (best-effort). */
  source: 'import' | 'reconstructed';
  /** NULL = not checked against ChargeOver; false = its truncated custom_2
   *  list disagrees, so treat the worklogs as suspect. */
  verified_against_chargeover: boolean | null;
  /** Side-by-side comparison against ChargeOver's stored list. Null when no
   *  verify pass has run for this line. */
  chargeover_diff: WorklogIdDiff | null;
  worklogs: InvoiceWorklog[];
  /** Summed billable hours of the captured worklogs. The invoice quantity is
   *  rounded UP to the nearest 15 min per week, so a small excess in
   *  line_quantity is expected; a large gap means worklogs are missing. */
  worklog_hours: number;
}

export interface InvoiceWorklogDetail {
  chargeover_invoice_id: string;
  captured_at: string | null;
  lines: InvoiceWorklogLine[];
}

export interface InvoiceDetailCoverage {
  invoices_with_detail: number;
  lines_imported: number;
  lines_reconstructed: number;
  lines_mismatched: number;
  worklogs: number;
}

export interface InvoiceDetailBackfillResult {
  scanned: number;
  reconstructed: number;
  no_worklogs: number;
  verified_match: number;
  verified_mismatch: number;
  verify_unavailable: number;
  errors: number;
  remaining: number;
}

export interface InvoiceReportInvoice {
  chargeover_invoice_id: string;
  invoice_number: string | null;
  raw_status: string | null;
  lifecycle: InvoiceLifecycle;
  invoice_date: string | null;
  date_issued: string | null;
  due_date: string | null;
  days_past_due: number | null;
  total_amount: number;
  paid_amount: number;
  balance_amount: number;
  writeoff_amount: number;
  credit_amount: number;
  /** "Our component" of the invoice — sum of non-fee line_totals. NULL
   *  when line_items haven't been seen yet (sync without line_items in
   *  the response); UI falls back to total_amount with a hint. */
  services_amount: number | null;
  /** CC processing / convenience fees broken out separately. NULL when
   *  line_items not yet observed. */
  fees_amount: number | null;
  /** Sum of this invoice's "Deposit" lines — a prepayment against work billed
   *  later, so excluded from billed_net. */
  deposit_total: number;
  /** Deposit credit ChargeOver has drawn down against this invoice. Lives on
   *  the transaction, so balance_amount does NOT reflect it — balance_net does. */
  deposit_applied: number;
  /** total_amount less the deposit portion: what this invoice actually bills
   *  for, and what the client/engagement totals roll up. */
  billed_net: number;
  /** What is genuinely still collectable. Zero once ChargeOver calls the
   *  invoice paid or void, whatever balance_amount says; otherwise
   *  balance_amount less any deposit drawn against it. Display and filter on
   *  this, or a settled invoice reads as owing money while its client total
   *  correctly says paid up. */
  balance_net: number;
  /** The part of a PAID invoice its own amount columns don't account for —
   *  ChargeOver settled it somewhere it doesn't expose on the invoice. Shown on
   *  the row so a $0 balance in ORP against a non-zero one in CO is explained
   *  rather than looking like ORP lost money. */
  settled_off_invoice: number;
  /** Customer-facing PDF URL (CO token-gated). NULL until next sync. */
  url_pdflink: string | null;
  /** At least one weekly line where a verify pass found ChargeOver's worklog
   *  list and ORP's disagree. Means "known to disagree", never "not known to
   *  agree" — unverified invoices read false. */
  has_chargeover_discrepancy: boolean;
  /** Customer-level unapplied credit (deposit + straight credits). Surfaced
   *  per-invoice so a draft row can badge "this customer has $X in credit
   *  you could apply before approving". Zero when none. */
  customer_unapplied_credit: number;
  /** Customer-level sum of issued-but-not-yet-paid deposit invoices. The
   *  credit memo for these doesn't exist yet (it materializes on
   *  closed-paid via webhook) but ops should still see the deposit is
   *  in flight before approving a draft T&M. Zero when none. */
  customer_pending_deposit: number;
}

export interface InvoiceReportEngagement {
  engagement_jira_issue_id: string;
  engagement_jira_issue_key: string;
  engagement_summary: string;
  invoices: InvoiceReportInvoice[];
  billed_total: number;
  paid_total: number;
  overdue_balance: number;
  outstanding_balance: number;
  writeoff_total: number;
  credit_total: number;
  draft_total: number;
  deposit_remaining: number;
  credit_remaining: number;
}

export interface InvoiceReportClient {
  id: string;
  tempo_account_id: string | null;
  tempo_account_name: string | null;
  chargeover_customer_id: string | null;
  chargeover_display_name: string | null;
  /** False when the ChargeOver customer has no external_key — no Tempo
   *  linkage. UI shows a "No external key" tag for these rows. */
  has_external_key: boolean;
  engagements: InvoiceReportEngagement[];
  orphan_invoices: InvoiceReportInvoice[];
  billed_total: number;
  paid_total: number;
  overdue_balance: number;
  outstanding_balance: number;
  writeoff_total: number;
  credit_total: number;
  deposit_total_issued: number;
  deposit_remaining: number;
  credit_total_issued: number;
  credit_remaining: number;
  draft_total: number;
  expected_to_collect: number;
}

export interface InvoiceReportMonthPoint { month: string; value: number }
export interface InvoiceReportMonthlyMetric {
  current: number;
  previous: number;
  history: InvoiceReportMonthPoint[];
}
export interface InvoiceReportMonthly {
  current_month: string;
  previous_month: string;
  billed:             InvoiceReportMonthlyMetric;
  paid:               InvoiceReportMonthlyMetric;
  deposits_collected: InvoiceReportMonthlyMetric;
}

export interface InvoiceReportParent {
  chargeover_customer_id: string;
  name: string;
  synthetic: boolean;
  clients: InvoiceReportClient[];
  billed_total: number;
  paid_total: number;
  overdue_balance: number;
  outstanding_balance: number;
  writeoff_total: number;
  credit_total: number;
  deposit_total_issued: number;
  deposit_remaining: number;
  credit_total_issued: number;
  credit_remaining: number;
  draft_total: number;
  expected_to_collect: number;
}

export interface InvoiceReport {
  clients: InvoiceReportClient[];
  parents: InvoiceReportParent[];
  monthly: InvoiceReportMonthly;
  totals: {
    client_count: number;
    engagement_count: number;
    invoice_count: number;
    billed_total: number;
    paid_total: number;
    overdue_balance: number;
    outstanding_balance: number;
    writeoff_total: number;
    credit_total: number;
    deposit_total_issued: number;
    deposit_remaining: number;
    credit_total_issued: number;
    credit_remaining: number;
    draft_total: number;
    expected_to_collect: number;
    overdue_1_30_balance: number;
    overdue_31_60_balance: number;
    overdue_61_90_balance: number;
    overdue_91_plus_balance: number;
  };
}

export interface AppSetting {
  key: string;
  value: string;
  updated_at: string;
  updated_by: string | null;
}

export interface HarvestImportResult {
  parsed: number;
  skipped_bad_shape: number;
  users_known: number;
  users_placeholder: number;
  missing_deliverables_count: number;
  missing_deliverables_sample: string[];
  worklogs_already_imported: number;
  worklogs_inserted: number;
  bonus_snapshots: { users: number; months: number; rows: number } | null;
  dry_run: boolean;
}

export interface DeliverableRollupRow {
  jira_issue_id: string;
  jira_issue_key: string;
  summary: string;
  issue_type: 'Deliverable' | 'Overhead';
  status: string;
  completed_date: string | null;
  due_date: string | null;
  tempo_account_id: string | null;
  account_name: string | null;
  /** Parent Engagement — one level above Deliverable/Overhead. NULL on
   *  orphan rows that arrived without a parent_engagement_jira_issue_id. */
  engagement_jira_issue_id: string | null;
  engagement_jira_issue_key: string | null;
  engagement_summary: string | null;
  delivery_manager_display_name: string | null;
  solution_architect_display_name: string | null;
  hours: number;
  billable_rate: number;
  is_fixed_fee: boolean;
  shipped_value: number;
  wip_value: number;
  /** Forfeited value from Canceled deliverables (never ships, distinct bucket). */
  canceled_value: number;
  total_value: number;
}

export interface DeliverableRollupBucket {
  bucket_key: string;
  bucket_label: string;
  deliverables: DeliverableRollupRow[];
  shipped_total: number;
  wip_total: number;
  canceled_total: number;
  grand_total: number;
  deliverable_count: number;
}

export interface DeliverableRollupReport {
  filters: {
    start: string;
    end: string;
    issue_type: 'all' | 'deliverables' | 'overheads';
    group_by: 'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';
  };
  buckets: DeliverableRollupBucket[];
  totals: {
    shipped_total: number;
    wip_total: number;
    canceled_total: number;
    grand_total: number;
    deliverable_count: number;
  };
}

export interface EngagementBudgetReport {
  engagements: EngagementBudgetRow[];
  totals: {
    total_contract: number;
    total_burned: number;
    overrun_count: number;
    fixed_fee_count: number;
    /** Engagements with neither a Jira budget nor a fixed_fee_amount on any
     *  child deliverable. Surface so the UI can say "X engagements untracked". */
    no_budget_count: number;
    total_outstanding: number;
    total_overdue: number;
    engagement_count: number;
    /** Engagements with captured contract terms. */
    contracted_count: number;
    /** Per-threshold counts. Cumulative by nature — anything past SNE is also
     *  past high — so read them as levels, not as a partition. */
    over_low_count: number;
    over_median_count: number;
    over_high_count: number;
    /** Contract breach: burn past a shall-not-exceed ceiling. */
    over_sne_count: number;
    /** Fixed-fee work burned past its price — the overage comes off margin. */
    over_fixed_fee_count: number;
  };
}

export interface PeriodRow {
  attribution_month: string;
  total_shipped_value: number;
  live_shipped_value: number;
  record_count: number;
  has_snapshot: boolean;
  is_locked: boolean;
  is_approved: boolean;
  is_paid: boolean;
}

export interface ForecastUserMonth {
  attribution_month: string;
  actual_shipped: number;
  wip_due: number;
  projected_total: number;
  projected_tier: number | null;
  projected_multiplier: number | null;
  projected_bonus: number | null;
  gap_to_next_tier: number | null;
}

export interface ForecastUserRow {
  user: {
    jira_user_id: string;
    display_name: string;
    email: string;
    role: Role;
    bonus_role: BonusRole | null;
    title: string;
    team: string;
  };
  effective_bonus_role: BonusRole;
  months: ForecastUserMonth[];
}

export interface ForecastResponse {
  generated_at: string;
  months: string[];
  users: ForecastUserRow[];
  pool_by_month: Array<{
    attribution_month: string;
    total_projected_bonus: number;
    users_on_tier: number;
    total_users: number;
  }>;
}

// ─── Billing — receivables ────────────────────────────────────────────────────

export interface ReceivablesInvoice {
  invoiceId: string;
  invoiceNumber: string | null;
  externalKey: string | null;
  dueDate: string | null;
  status: string | null;
  total: number;
  payments: number;
  credits: number;
  writeoffs: number;
  balance: number;
}

export interface ReceivablesCredit {
  transactionId: string;
  transactionDate: string | null;
  amount: number;
  applied: number;
  unapplied: number;
}

export interface ReceivablesRow {
  tempoAccountId: string | null;
  accountName: string | null;
  chargeoverCustomerId: string | null;
  customerDisplayName: string | null;
  total: number;
  payments: number;
  credits: number;
  writeoffs: number;
  balance: number;
  availableCredit: number;
  expectedCollection: number;
  invoiceCount: number;
  creditCount: number;
  invoices: ReceivablesInvoice[];
  credits_detail: ReceivablesCredit[];
}

export interface ReceivablesTotals {
  total: number;
  payments: number;
  credits: number;
  writeoffs: number;
  balance: number;
  availableCredit: number;
  expectedCollection: number;
}

export interface ReceivablesReport {
  monthKey: string;
  range: { from: string; to: string };
  rows: ReceivablesRow[];
  totals: ReceivablesTotals;
}

export interface WebhookReceiptRow {
  id: string;
  /** ISO datetime — when the audit_log row was written. */
  created_at: string;
  /** Tied to the invoice when the event was an invoice-context webhook,
   *  null for pings and other non-invoice contexts. */
  entity_id: string | null;
  /** Free-form JSON the handler stamped: context_str, event, outcome,
   *  invoice_status_name / _str, customer_id, custom_2, auto_approved /
   *  reason, deposit_amount / transaction_id, error. */
  detail: Record<string, unknown> | null;
}

/** Which webhook side effect an invoice never received. */
export type CatchupAction = 'auto_approve' | 'deposit_credit';

export interface CatchupCounts {
  auto_approve: number;
  deposit_credit: number;
  total: number;
  /** Draft-lifecycle invoices that aren't ours but don't read as "Unapproved".
   *  Expected to be empty — anything here means ChargeOver labels the state
   *  differently on this tenant and the approval arm is matching nothing. */
  unmatched_draft_statuses: Array<{ raw_status: string | null; count: number }>;
}

export interface CatchupCandidate {
  chargeover_invoice_id: string;
  chargeover_customer_id: string | null;
  invoice_number: string | null;
  customer_name: string | null;
  account_name: string | null;
  tempo_account_id: string | null;
  raw_status: string | null;
  total_amount: number;
  deposit_total: number | null;
  invoice_date: string | null;
  paid_date: string | null;
  action: CatchupAction;
}

export interface CatchupOutcome {
  chargeover_invoice_id: string;
  invoice_number: string | null;
  customer_name: string | null;
  action: CatchupAction;
  result: 'applied' | 'skipped' | 'errored';
  detail: string;
  amount?: number;
}

export interface CatchupRunResult {
  dry_run: boolean;
  scanned: number;
  approved: number;
  credited: number;
  skipped: number;
  errored: number;
  remaining: number;
  outcomes: CatchupOutcome[];
}

export interface WritebackDetailRow {
  id: string;
  jira_issue_id: string;
  jira_issue_key: string;
  issue_type: string;
  attribution_month: string;
  shipped_date: string;
  shipped_value: string;
  state: 'pending' | 'done' | 'failed';
  attempt_count: number;
  last_error: string | null;
  last_attempted_at: string | null;
  enqueued_at: string;
}

export interface AuditEntry {
  id: string;
  actor_id: string | null;
  actor_display_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  target_display_name: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

// ─── Billing — async imports ─────────────────────────────────────────────────

export type BillingRunStatus = 'queued' | 'running' | 'success' | 'warning' | 'failed';

export interface BillingRunSummary {
  id: string;
  month: string;
  triggered_by_user_id: string | null;
  triggered_by_display_name: string | null;
  invoice_date: string;
  status: BillingRunStatus;
  started_at: string;
  ended_at: string | null;
  accounts_processed: number;
  invoices_created: number;
  invoices_updated: number;
  invoices_zeroed_out: number;
  accounts_errored: number;
  accounts_skipped: number;
  error_message: string | null;
}

export type BillingRunAccountStatus = 'pending' | 'created' | 'updated' | 'zeroed_out' | 'error' | 'skipped';

/** Stable error/skipped taxonomy. Server may add new tags over time; the UI
 *  treats unknown values as raw strings. */
export type BillingRunAccountErrorType =
  | 'no_billable_no_draft'
  | 'no_chargeover_customer'
  | 'missing_billing_item'
  | 'no_billing_item_definition'
  | 'fixed_fee_chargeover_customer'
  | 'existing_invoice_not_unapproved'
  | 'weekly_groups_zero_after_rounding'
  | 'invoice_create_failed'
  | 'invoice_update_failed'
  | 'zero_out_failed'
  | 'chargeover_timeout'
  | 'chargeover_rate_limited'
  | 'chargeover_http_error'
  | 'chargeover_network_error'
  | 'invoice_lookup_failed'
  | 'unknown';

export interface BillingRunAccount {
  id: string;
  account_key: string;
  account_name: string | null;
  status: BillingRunAccountStatus;
  chargeover_invoice_id: string | null;
  external_key: string | null;
  total_seconds: number;
  total_hours: number;
  total_value: number;
  error_message: string | null;
  /** Stable taxonomy tag, set when status is 'error' or 'skipped'. NULL for
   *  successful rows. Drives row expansion / grouping in the UI; the raw
   *  text stays in `error_message` and the structured payload in
   *  `error_detail`. */
  error_type: BillingRunAccountErrorType | string | null;
  /** Raw underlying error payload — for CO write failures carries
   *  { kind, status, method, path, response_body, attempts }; for pre-flight
   *  errors carries { message, accountKey }. NULL on non-error rows. */
  error_detail: Record<string, unknown> | null;
  processed_at: string | null;
}

// ─── Billing preview ─────────────────────────────────────────────────────────

export interface BillingMonthRange {
  from: string;
  to: string;
  capped: boolean;
  capInfo?: { rawMonthEnd: string; cappedAt: string; reason: string };
}

export interface BillingUserRow {
  authorName: string;
  userId: string | null;
  worklogCount: number;
  totalSeconds: number;
  totalHours: number;
  flaggedSeconds: number;
  flaggedHours: number;
}

export interface BillingAccountRow {
  accountKey: string;
  accountName: string;
  accountId: number | null;
  leadAccountId: string | null;
  leadDisplayName: string | null;
  worklogCount: number;
  totalSeconds: number;
  totalHours: number;
  flaggedSeconds: number;
  flaggedHours: number;
}

export interface BillingWeeklyGroup {
  accountKey: string;
  weekStart: string;
  weekEnd: string;
  totalSeconds: number;
  flaggedSeconds: number;
  worklogIds: string[];
}

export interface BillingStaleDraftRow {
  accountKey: string;
  accountName: string;
  chargeoverInvoiceId: string;
  externalKey: string;
  rawStatus: string;
  invoiceNumber: string | null;
  totalAmount: number;
  /** Customer's unapplied credit balance — shown so the operator can see
   *  there's a credit to apply before letting the import zero out the
   *  draft. */
  customerUnappliedCredit: number;
  /** Customer's sum of issued-but-unpaid deposit invoices. */
  customerPendingDeposit: number;
}

export interface BillingSyncStatus {
  lastSuccessfulSyncAt: string | null;
  minutesSinceLastSync: number | null;
  thresholdMinutes: number;
  isStale: boolean;
}

export interface BillingEnrichedWorklog {
  tempoWorklogKey: string;
  accountKey: string;
  accountName: string | null;
  accountId: number | null;
  billableSeconds: number;
  isFlaggedForDMReview: boolean;
  authorName: string;
  userId: string | null;
  workDate: string | null;
  description: string;
}

export interface BillingPreview {
  monthKey: string;
  range: BillingMonthRange;
  allowedMonths: string[];
  defaultInvoiceDate: string;
  totals: {
    worklogs: number;
    seconds: number;
    hours: number;
    flaggedSeconds: number;
    flaggedHours: number;
  };
  byUser: BillingUserRow[];
  byAccount: BillingAccountRow[];
  enrichedWorklogs: BillingEnrichedWorklog[];
  weeklyGroupsByAccount: Array<{ accountKey: string; groups: BillingWeeklyGroup[] }>;
  clientEngagementAccountKeys: string[];
  staleDraftInvoices: BillingStaleDraftRow[];
  syncStatus: BillingSyncStatus;
}

// ─── Tempo-report reconcile ──────────────────────────────────────────────────

export interface BillingReconcileIssue {
  worklogId: string;
  accountKey: string;
  accountName: string;
  workDate: string;
  author: string;
  description: string;
  /** Billable seconds ORP attributes (null when ORP doesn't have the worklog). */
  orpSeconds: number | null;
  /** Billable seconds the Tempo report shows (null when Tempo no longer has it). */
  tempoSeconds: number | null;
  /** For account-mismatch rows: the account ORP attributes it to. */
  orpAccountKey?: string;
}

export interface BillingReconcileAccountRow {
  accountKey: string;
  accountName: string;
  orpSeconds: number;
  tempoSeconds: number;
  deltaSeconds: number;   // orp - tempo (positive = ORP over-counts)
  orpHours: number;
  tempoHours: number;
  deltaHours: number;
  issues: {
    tempoOnly: BillingReconcileIssue[];     // in Tempo, missing from ORP
    orpOnly: BillingReconcileIssue[];       // in ORP, gone from Tempo (phantom)
    hoursDiffer: BillingReconcileIssue[];   // same worklog, different billable seconds
    accountDiffer: BillingReconcileIssue[]; // same worklog, different account
  };
}

export interface BillingReconcileResult {
  month: string;
  reportRows: number;
  range: BillingMonthRange;
  totals: {
    orpSeconds: number;
    tempoSeconds: number;
    deltaSeconds: number;
    orpHours: number;
    tempoHours: number;
    deltaHours: number;
    orpWorklogs: number;
    tempoWorklogs: number;
    matched: number;
    tempoOnly: number;
    orpOnly: number;
    hoursDiffer: number;
    accountDiffer: number;
    tempoOutsideWindow: number;
  };
  accounts: BillingReconcileAccountRow[];
}
